// Default baked lighting: indirect light computed offline in Blender Cycles,
// drawn live as lightmaps. The sun's direct light and shadows stay real-time; everything else that the walkthrough
// fakes (window fill lights, the daylight volume, the room bounce, the hemisphere light, the lamp rectangles) is
// replaced by the bake.
//
// Data (made by the bake pipeline in bake/, see bake/README.md):
//   lightmap_uv.bin : for each static mesh ("unit"), the lightmap re-indexing: new vertex -> original vertex (xref),
//                     the new triangle list (same triangles, same order, so material groups still apply) and
//                     normalised 16-bit lightmap UVs. Units are matched to the live meshes by the stable id batch.js
//                     gives every mesh that holds still (carried into the export); sidecars without ids, and any id that
//                     misses, fall back to the original vertex count and world bounding box.
//   <slot>/sky.hdr, sunind.hdr, lamps.hdr : the three layers. Sky = sky light and its bounces; sunind = the sun's
//                     bounces only; lamps = the lamps at unit level, direct and bounced. Summed here on the GPU.
// The bakes hold Cycles' diffuse light without colour (radiance / albedo = irradiance / PI), hence the PI below.
import * as THREE from 'three';
import { warmPrograms, shaderYield } from './shader_warmup.js';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js';
import { CONTACT, CONTACT_SHADER } from './contact.js';
import { blenderAgX, loadAgXLuts } from './agx.js';
import { createBakedAutoExposure } from './auto_exposure.js';
import { createWhiteBalance } from './white_balance.js';
import { createPuddles, scanPuddles, markPuddleGround, PUDDLE_UNIFORMS } from './puddles.js';
import { bakeFoliage, loadFoliage, FOLIAGE_UNIFORMS, shaderPatchCount, assertShaderPatches } from './foliage_bake.js';
import { lightmapAssets, lightmapBitmap } from './lightmap_assets.js';
import { SLOTS, discoverSlots, initialSlot, nextSlot, validEncoding } from './lighting_slots.js';
import { createSlotRuntime } from './lightmap_slots.js';
import { crossfadeSun } from './sun_crossfade.js';
import { createLampGroups, lampGroupsWanted, loadGroupProbes, validGroupEncoding } from './lamp_groups.js';
import { createSlotProgress } from './slot_progress.js';
import { TIERS } from './tier.js';
import { bakedResolution } from './baked_mode.js';
import { auditPrograms } from './sampler_budget.js';
import { pieces as planPieces, plan } from './plan.js';
import { PCSS_TAPS } from './shaders/pcss.glsl.js';
import { RGBM_DECODE, TONE_DECODE, lmSampleGLSL } from './shaders/lightmap.glsl.js';
import { reflGLSL } from './shaders/reflection.glsl.js';
import { bakedFeatures } from './shaders/features.js';
import { installBaked, uninstallBaked, ownShader, copyOwnShader, materialOptions, sunDepthShader, dryCompile } from './shaders/baked_material.js';
import { bakedEmission, glowLevel, lightingOf, meshLighting, bakedLiveLevel, LM_PER_BAKE_UNIT } from './lighting_roles.js';
import { VolumetricPass, installVolume } from './volumetric.js';
import { featureWanted } from './volumetric_slots.js';

// The bake's sun (scripts/make_tasks.py, slot "sunrise"): bearing, height, strength (W/m2 = irradiance) and colour.
export { SLOTS } from './lighting_slots.js';

// After rain, outdoors (the same rules as the Blender scene, scripts setup.py): ground darker and glossy with puddles
// where a world-space noise dips, plants with a little sheen, outdoor furniture slightly darker and smoother. Each
// mesh's class is its manifest record's weather (lighting_roles.js weatherOf).
function wetten(c, cls, wet) {
  if (!cls || !(wet > 0)) return;
  const mix = THREE.MathUtils.lerp;
  if (cls === 'ground') { c.color.multiplyScalar(mix(1, 0.62, wet)); c.roughness = mix(c.roughness, Math.max(0.06, c.roughness * 0.3), wet); }
  else if (cls === 'plant') c.roughness = mix(c.roughness, Math.max(0.12, c.roughness * 0.45), wet);
  else { c.color.multiplyScalar(mix(1, 0.85, wet)); c.roughness = mix(c.roughness, Math.max(0.08, c.roughness * 0.55), wet); }
}

// ---- Soft sun shadows (PCSS), as Blender's sun has a size: the penumbra grows with the distance to whatever blocks the
// light, so leaves 20 m away cast wide soft dapples while a window frame stays crisp. three's shadow map only answers
// "lit or not", so the sun's depth is rendered once more into a readable texture (alpha cut-outs kept) and filtered here.
const SUN_RADIUS = THREE.MathUtils.degToRad(0.265);   // the real sun's angular radius (0.53 degrees across): crisp penumbrae like real sunlight
export { PCSS_TAPS };
const PCSS_TIER = { hq: 0, laptop: 1, mobile: 2 };
// Two depth targets: `base` holds every caster that holds still, `rt` (sampled) is base's depth plus the "hot" casters,
// the ones moving now: a door in its swing, the garage door, a toggle, a carried chair (hot from its first change until
// 2 s after its last), the pets and the visitor (always hot). A refresh while they move is a depth copy and a few
// draws; base is redrawn only when a caster turns hot (once per interaction: it must leave base), on load/tier changes,
// and after the slow scan finds an untracked caster moving. Cooled casters return to base at its next redraw.
// Shader-only motion (garden wind in custom depth materials) stays a snapshot, and so does anything that never stops
// moving on its own (ambient, below).
// A caster leaving base or returning to it redraws only base's texels around it (REGION_LAYER: the casters whose bounds
// reach that rect, drawn with the full map's camera under a scissor, so every texel comes out as a full redraw would make
// it): about a hundred draws instead of every caster in the garden (~55 ms of CPU on a 2.1 GHz Xeon at each door click,
// twice). Promotions by the slow scan, tier and load changes still redraw it all. ?pcssregion=0: full redraws only.
const DYN_LAYER = 29, REGION_LAYER = 28;
const PARTIAL_BASE = new URLSearchParams(globalThis.location?.search ?? '').get('pcssregion') !== '0';
function createSunPCSS(renderer, scene, sun, size = 4096) {
  const target = n => { const t = new THREE.WebGLRenderTarget(n, n, { depthBuffer: true, format: THREE.RedFormat, generateMipmaps: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter }); t.depthTexture = new THREE.DepthTexture(n, n, THREE.FloatType); return t; };
  const rt = target(size), base = target(size);
  const uniforms = { uSunDepth: { value: rt.depthTexture }, uSunTexel: { value: 1 / size }, uSunDepthRange: { value: 1 }, uSunWidth: { value: 1 }, uSunTan: { value: Math.tan(SUN_RADIUS) }, uSunFrame: { value: 0 }, uSunTaps: { value: 0 } };
  const cache = new Map(), entries = new Map(), swaps = [], roots = new Set(), always = new Set(), promoted = new Set(), rigid = new Map();
  let dynamic = [], statics = [], cursor = 0;
  let version = 0, clock = 1, now = 0, wasMoving = false, dirty = true, staticDirty = true, classifyDirty = true, staticDue = 0;
  // region: world box of base texels to redraw (null: none); fullDue: the pending staticDue needs a full redraw (a
  // promotion: the caster's old pose is unknown); foldDue: it folds cooled casters back
  let region = null, fullDue = false, foldDue = false, lights = [];
  const stats = { casters: 0, tracked: 0, hot: 0, ambient: 0, staticRenders: 0, partialRenders: 0, regionDraws: 0, refreshes: 0, staticMs: 0, refreshMs: 0 };
  const boxOf = o => new THREE.Box3().setFromObject(o);
  const addRegion = box => { if (!box.isEmpty()) (region ??= new THREE.Box3()).union(box); };
  // a caster that turns hot leaves base: its texels there are redrawn without it
  const leaveBase = e => { if (!PARTIAL_BASE) { staticDirty = true; return; } if (e.inBase) addRegion(e.baseBox); e.inBase = false; };
  const depthOf = m => {
    if (!m) return m;
    let d = cache.get(m);
    if (!d) {
      d = new THREE.MeshDepthMaterial(); cache.set(m, d);
      // Keep vertex/alpha deformation, with raster GI removed before the depth hook is captured.
      const { compile, key } = sunDepthShader(m);
      d.onBeforeCompile = compile;
      d.customProgramCacheKey = () => key;
      d.onBeforeRender = m.onBeforeRender;
    }
    if (d.userData.sourceVersion !== m.version || !!d.map !== !!m.map || !!d.alphaMap !== !!m.alphaMap || d.side !== (m.shadowSide ?? THREE.DoubleSide)) d.needsUpdate = true;
    d.userData.sourceVersion = m.version;
    for (const k of ['map', 'alphaMap', 'alphaTest', 'displacementMap', 'displacementScale', 'displacementBias', 'clipShadows', 'clippingPlanes']) d[k] = m[k];
    d.defines = m.defines; d.vertexColors = m.vertexColors; d.flatShading = m.flatShading;
    d.side = m.shadowSide ?? THREE.DoubleSide; d.visible = m.visible; d.wireframe = m.wireframe;
    return d;
  };
  const swapIn = (o, e) => {
    const depth = o.customDepthMaterial;
    if (Array.isArray(o.material)) { const a = e.depths ??= []; a.length = o.material.length; for (let i = 0; i < a.length; i++) a[i] = depth ?? depthOf(o.material[i]); o.material = a; }
    else o.material = depth ?? depthOf(o.material);
  };
  // tracked casters (checked every frame): under a tracked root, skinned, or promoted by the slow scan. The hot ones
  // carry a scene layer, which the moving-caster pass draws alone.
  const rootOf = o => { for (let p = o; p; p = p.parent) if (roots.has(p)) return p; return null; };
  const heat = (o, e, hot) => { e.hot = hot; e.cold = false; if (hot) o.layers.enable(DYN_LAYER); else o.layers.disable(DYN_LAYER); };
  const classify = () => {
    classifyDirty = false; dynamic = []; statics = [];
    for (const r of roots) if (r.parent) r.updateWorldMatrix(true, true);   // snapshots of current, not stale, poses
    scene.traverse(o => {
      if (!o.isMesh) return;
      if (!o.castShadow) { o.layers.disable(DYN_LAYER); return; }
      let e = entries.get(o); if (!e) { e = {}; entries.set(o, e); changed(o, e); e.plant = meshLighting(o).sun.track === 'plant'; }
      const root = rootOf(o);
      e.always = o.isSkinnedMesh || always.has(root); e.rigid = rigid.get(root) ?? null;
      if (root || e.always || promoted.has(o)) { dynamic.push(o); heat(o, e, e.always || (!!e.hot && !e.ambient)); } else { statics.push(o); heat(o, e, false); }
    });
    const live = new Set([...dynamic, ...statics]); for (const o of entries.keys()) if (!live.has(o)) entries.delete(o);
    stats.casters = live.size; stats.tracked = dynamic.length; cursor = 0;
    lights = []; scene.traverse(o => { if (o.isLight) lights.push(o); });
  };
  const sync = () => {
    sun.updateWorldMatrix(true, false); sun.target.updateWorldMatrix(true, false); sun.shadow.updateMatrices(sun);
    const cam = sun.shadow.camera; uniforms.uSunDepthRange.value = cam.far - cam.near; uniforms.uSunWidth.value = cam.right - cam.left;
    return cam;
  };
  const draw = (into, cam, statics) => {
    const bg = scene.background, override = scene.overrideMaterial, auto = renderer.autoClear, shadow = renderer.shadowMap?.enabled, mask = cam.layers.mask;
    let n = 0;
    try {
      if (statics) {
        // every renderable: non-casters and hot casters hidden, still casters drawn with depth materials; casters that
        // cooled down (or turned ambient) since the last redraw return here
        for (const o of dynamic) { const e = entries.get(o); if (e.hot && (e.cold || e.ambient)) heat(o, e, false); }
        scene.traverse(o => {
          if (!o.isMesh && !o.isLine && !o.isPoints && !o.isSprite) return;
          const e = swaps[n] ??= {}; n++; e.o = o; e.material = o.material; e.visible = o.visible;
          if (!o.isMesh || !o.castShadow || o.layers.isEnabled(DYN_LAYER)) { o.visible = false; return; }
          swapIn(o, e);
        });
      } else {
        for (const o of dynamic) { if (!o.layers.isEnabled(DYN_LAYER)) continue; const e = swaps[n] ??= {}; n++; e.o = o; e.material = o.material; e.visible = o.visible; if (o.castShadow) swapIn(o, e); else o.visible = false; }
        cam.layers.set(DYN_LAYER);
      }
      scene.background = scene.overrideMaterial = null; renderer.autoClear = statics;
      if (renderer.shadowMap) renderer.shadowMap.enabled = false;
      renderer.setRenderTarget(into); renderer.render(scene, cam);
    } finally {
      cam.layers.mask = mask; renderer.autoClear = auto; scene.background = bg; scene.overrideMaterial = override;
      if (renderer.shadowMap) renderer.shadowMap.enabled = shadow;
      for (let i = 0; i < n; i++) { const e = swaps[i]; e.o.material = e.material; e.o.visible = e.visible; }
    }
  };
  const copyBase = () => {
    // both framebuffers exist once three has bound them; then a depth blit, and three's state cache is reset
    renderer.setRenderTarget(base); renderer.setRenderTarget(rt);
    const gl = renderer.getContext?.(), P = renderer.properties, n = rt.width;
    if (!gl?.blitFramebuffer || !P) return;   // test doubles
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, P.get(base).__webglFramebuffer); gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, P.get(rt).__webglFramebuffer);
    gl.disable(gl.SCISSOR_TEST); gl.depthMask(true);
    gl.blitFramebuffer(0, 0, n, n, 0, 0, n, n, gl.DEPTH_BUFFER_BIT, gl.NEAREST);
    renderer.resetState();
  };
  // Redraw all of base; every tracked caster drawn into it is in base at its box now.
  const fullBase = cam => {
    draw(base, cam, true); staticDirty = false; region = null; foldDue = false;
    for (const o of dynamic) { const e = entries.get(o); if (!o.layers.isEnabled(DYN_LAYER)) { e.inBase = !!e.visible; e.baseBox = boxOf(o); } else e.inBase = false; }
  };
  // Redraw base's texels under `region` (see REGION_LAYER). Cooled casters due back fold in here, at their pose now.
  // Returns false (nothing drawn), true, or 'full' (the region covers most of the map: redraw it all instead).
  const v = new THREE.Vector3(), sphere = new THREE.Sphere(), regionSwaps = [];
  const drawRegion = cam => {
    if (foldDue) {
      for (const o of dynamic) { const e = entries.get(o); if (e.hot && (e.cold || e.ambient)) { heat(o, e, false); if (e.visible) addRegion(boxOf(o)); } }
      foldDue = false;
    }
    const box = region?.expandByScalar(0.1); region = null;
    if (!box) return false;
    // the region's texel rect (two texels of slack), and that rect back in the light's frame
    const n = base.width, W = cam.right - cam.left, H = cam.top - cam.bottom;
    let lx0 = Infinity, lx1 = -Infinity, ly0 = Infinity, ly1 = -Infinity;
    for (let i = 0; i < 8; i++) {
      v.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).applyMatrix4(cam.matrixWorldInverse);
      lx0 = Math.min(lx0, v.x); lx1 = Math.max(lx1, v.x); ly0 = Math.min(ly0, v.y); ly1 = Math.max(ly1, v.y);
    }
    const x0 = Math.max(0, Math.floor((lx0 - cam.left) / W * n) - 2), x1 = Math.min(n, Math.ceil((lx1 - cam.left) / W * n) + 2);
    const y0 = Math.max(0, Math.floor((ly0 - cam.bottom) / H * n) - 2), y1 = Math.min(n, Math.ceil((ly1 - cam.bottom) / H * n) + 2);
    if (x1 <= x0 || y1 <= y0) return false;   // off the map: nothing to redraw
    if ((x1 - x0) * (y1 - y0) > n * n / 3) return 'full';   // most of it: as cheap to draw it all
    const ux0 = cam.left + x0 / n * W, ux1 = cam.left + x1 / n * W, uy0 = cam.bottom + y0 / n * H, uy1 = cam.bottom + y1 / n * H;
    const bg = scene.background, override = scene.overrideMaterial, auto = renderer.autoClear, shadow = renderer.shadowMap?.enabled, mask = cam.layers.mask;
    let k = 0;
    try {
      for (const list of [statics, dynamic]) for (const o of list) {
        if (o.layers.isEnabled(DYN_LAYER) || !o.castShadow) continue;
        const g = o.geometry, s = o.isInstancedMesh ? (o.boundingSphere ?? (o.computeBoundingSphere(), o.boundingSphere)) : (g.boundingSphere ?? (g.computeBoundingSphere(), g.boundingSphere));
        sphere.copy(s).applyMatrix4(o.matrixWorld); v.copy(sphere.center).applyMatrix4(cam.matrixWorldInverse);
        const r = sphere.radius + 0.5;   // slack for vertex wind in the depth shaders (leaves, grass)
        if (v.x + r < ux0 || v.x - r > ux1 || v.y + r < uy0 || v.y - r > uy1) continue;
        const e = entries.get(o), w = regionSwaps[k] ??= {}; k++; w.o = o; w.material = o.material;
        o.layers.enable(REGION_LAYER); swapIn(o, w);
        if (list === dynamic) { e.inBase = !!e.visible; e.baseBox = boxOf(o); }
      }
      for (const l of lights) l.layers.enable(REGION_LAYER);   // the same lights as a full redraw: the same depth programs
      scene.background = scene.overrideMaterial = null; renderer.autoClear = true;
      if (renderer.shadowMap) renderer.shadowMap.enabled = false;
      cam.layers.set(REGION_LAYER);
      base.scissor.set(x0, y0, x1 - x0, y1 - y0); base.scissorTest = true;   // the clear and the draws stay inside the rect
      renderer.setRenderTarget(base); renderer.render(scene, cam);
    } finally {
      base.scissorTest = false; base.scissor.set(0, 0, n, n);
      cam.layers.mask = mask; renderer.autoClear = auto; scene.background = bg; scene.overrideMaterial = override;
      if (renderer.shadowMap) renderer.shadowMap.enabled = shadow;
      for (const l of lights) l.layers.disable(REGION_LAYER);
      for (let i = 0; i < k; i++) { const w = regionSwaps[i]; w.o.material = w.material; w.o.layers.disable(REGION_LAYER); }
    }
    stats.partialRenders++; stats.regionDraws = k;
    return true;
  };
  const refresh = (full = false) => {
    if (classifyDirty) classify();
    const prev = renderer.getRenderTarget(), face = renderer.getActiveCubeFace?.(), mip = renderer.getActiveMipmapLevel?.(), t0 = performance.now();
    try {
      const cam = sync();
      let redrawn = false;
      if (full || staticDirty) { fullBase(cam); redrawn = true; }
      else if (region || foldDue) { const r = drawRegion(cam); if (r === 'full') fullBase(cam); redrawn = r !== false; }
      if (redrawn) { stats.staticRenders++; stats.staticMs = +(performance.now() - t0).toFixed(1); }
      copyBase();
      stats.hot = dynamic.reduce((n, o) => n + o.layers.isEnabled(DYN_LAYER), 0);
      if (stats.hot) draw(rt, cam, false);
      version++; dirty = false; clock = 0; stats.refreshes++; stats.refreshMs = +(performance.now() - t0).toFixed(1);
      for (const e of entries.values()) e.pending = false;
    } finally { renderer.setRenderTarget(prev, face, mip); }
  };
  // the shader hook a material's depth copy runs (its vertex deformation), once per material
  const hooks = new WeakMap(), hookOf = m => { if (!m) return null; let h = hooks.get(m); if (h === undefined) { h = ownShader(m).compile ?? null; hooks.set(m, h); } return h; };
  // Visibility (LOD levels are three's to switch: not a change), world matrices (doors, toggles, carried objects and
  // parent transforms), skin, morph and instance data, geometry and material changes. Snapshots are reused.
  const changedArray = (e, key, a) => {
    let old = e[key], changed = !old || old.length !== a.length;
    if (changed) old = e[key] = new Float64Array(a.length);
    for (let i = 0; i < a.length; i++) { if (old[i] !== a[i]) changed = true; old[i] = a[i]; }
    return changed;
  };
  const changed = (o, e) => {
    let visible = true, top = o, changed = false;
    for (let p = o; p; p = p.parent) { if (!p.visible && !p.parent?.isLOD) visible = false; top = p; }
    if (top !== scene) { classifyDirty = true; return true; }   // removed from the scene
    if (visible !== e.visible) { e.visible = visible; changed = true; }
    if (!visible) return changed;
    changed = changedArray(e, 'matrix', o.matrixWorld.elements) || changed;
    if (o.skeleton) { e.bones ??= []; for (let i = 0; i < o.skeleton.bones.length; i++) changed = changedArray(e.bones, i, o.skeleton.bones[i].matrixWorld.elements) || changed; }
    if (o.morphTargetInfluences) changed = changedArray(e, 'morph', o.morphTargetInfluences) || changed;
    if (e.instance !== o.instanceMatrix?.version || e.count !== o.count || e.position !== o.geometry.attributes.position?.version || e.geometry !== o.geometry || e.depth !== o.customDepthMaterial || e.castShadow !== o.castShadow) {
      if (e.castShadow !== undefined && e.castShadow !== o.castShadow) classifyDirty = true;
      e.instance = o.instanceMatrix?.version; e.count = o.count; e.position = o.geometry.attributes.position?.version; e.geometry = o.geometry; e.depth = o.customDepthMaterial; e.castShadow = o.castShadow; changed = true;
    }
    const multi = Array.isArray(o.material), n = multi ? o.material.length : 1, ms = e.materials ??= [];
    if (ms.length !== n) { ms.length = n; changed = true; }
    for (let i = 0; i < n; i++) {
      const m = multi ? o.material[i] : o.material, a = ms[i] ??= {};
      // what the depth pass reads (not the version: environment maps and the like recompile without changing depth; not
      // the material object either: the hover highlight swaps in a tinted copy with the same shader hook, and counting
      // that as motion redrew every still caster each time the crosshair crossed a seat)
      const hook = hookOf(m);
      if (a.hook !== hook || a.alpha !== m?.alphaTest || a.visible !== m?.visible || a.map !== m?.map || a.alphaMap !== m?.alphaMap || a.side !== (m?.shadowSide ?? m?.side) || a.disp !== m?.displacementMap) {
        a.hook = hook; a.alpha = m?.alphaTest; a.visible = m?.visible; a.map = m?.map; a.alphaMap = m?.alphaMap; a.side = m?.shadowSide ?? m?.side; a.disp = m?.displacementMap; changed = true;
      }
    }
    return changed;
  };
  // main.js calls this after every animation/interaction update of the frame. Moving set: every frame; still set: a
  // slice per frame (all of it about every four seconds), and a still caster found changed joins the moving set.
  const update = (dt = 0) => {
    if (classifyDirty) classify();
    let moving = false, settled = false; now += dt;
    for (const r of roots) {
      if (!r.parent) continue;
      const k = rigid.get(r);
      if (!k) { r.updateWorldMatrix(true, true); continue; }
      // a rigid root: its own world matrix and the visibility up to the scene stand for every caster below it
      r.updateWorldMatrix(true, false);
      let shown = true; for (let p = r; p; p = p.parent) if (!p.visible) { shown = false; break; }
      const quiet = k.shown === shown && changedArray(k, 'matrix', r.matrixWorld.elements) === false;
      if (!quiet) r.updateWorldMatrix(false, true);
      k.shown = shown; k.quiet = quiet;
    }
    for (const o of promoted) o.updateWorldMatrix(true, false);
    for (const o of dynamic) {
      const e = entries.get(o), c = e.rigid?.quiet && !e.always ? false : changed(o, e);
      if (e.ambient) continue;
      // a motion streak: changes no more than 0.5 s apart (some animations skip frames)
      if (c) { if (!(now - e.last <= 0.5)) e.first = now; e.last = now; }
      // something that never stops on its own clock (a promoted plant swaying: 2 s; a tracked part: 20 s) becomes
      // ambient: back into base as a snapshot and no longer a reason to refresh, so the soft shadows and the light
      // shafts' history can settle. Pets and the visitor never do.
      if (!e.always && now - e.last <= 0.5 && now - e.first > (promoted.has(o) ? 2 : 20)) { e.ambient = true; stats.ambient++; staticDue = 0.5; continue; }
      if (c && !e.hot) { heat(o, e, true); if (promoted.has(o)) { staticDue = 0.5; fullDue = true; } else leaveBase(e); }   // must leave base now
      // cooled down 2 s after its last change: folded back into base by one redraw a second later (keeps refreshes cheap)
      if (c) e.cold = false; else if (e.hot && !e.always && !e.cold && now - e.last > 2) { e.cold = true; staticDue = Math.max(staticDue, 1); }
      settled ||= !!(e.moving && !c && e.pending); e.moving = c; e.pending ||= c; moving ||= c;
    }
    const k = Math.min(statics.length, Math.ceil(statics.length / 120));
    for (let i = 0; i < k; i++) {
      const o = statics[cursor++ % statics.length], e = entries.get(o);
      // tracked and hot from now on; base is redrawn without it once promotions pause for half a second. Plants sway
      // on their own clock: a snapshot, as before (their near/far copies swap too), never promoted.
      if (changed(o, e) && !e.plant) { promoted.add(o); e.hot = true; classifyDirty = true; staticDue = 0.5; fullDue = true; moving = true; e.moving = e.pending = true; }
    }
    if (staticDue > 0 && (staticDue -= dt) <= 0) { if (fullDue || !PARTIAL_BASE) staticDirty = true; else foldDue = true; fullDue = false; moving = true; }
    dirty ||= moving; clock += dt;
    if (dirty && (clock >= 0.05 || settled || (!moving && wasMoving))) refresh();
    wasMoving = moving;
  };

  const invalidate = () => { classifyDirty = staticDirty = dirty = true; clock = 1; };
  // subtrees whose casters move (doors, toggles, movables, pets, the visitor): checked every frame
  // rigid: roots whose parts never move, show or hide against them (a carried chair, a room's fit-out; nothing animated,
  // toggled or skinned below): while the root's world matrix and visibility hold, its casters are not scanned one by one
  // (the per-frame scan of ~1700 casters cost ~1.5 ms on a 2.1 GHz Xeon)
  const rigidOk = r => { let ok = true; r.traverse(o => { if (o.userData.animate || o.userData.toggle || o.userData.door || o.isSkinnedMesh) ok = false; }); return ok; };
  const track = (objects, hot = false, still = false) => { for (const o of [].concat(objects)) if (o) { roots.add(o); if (hot) always.add(o); if (still && !hot && rigidOk(o)) rigid.set(o, {}); } invalidate(); };
  const setSize = n => { if (rt.width === n) return; rt.setSize(n, n); base.setSize(n, n); uniforms.uSunTexel.value = 1 / n; invalidate(); };
  const setTier = name => { uniforms.uSunTaps.value = PCSS_TIER[name] ?? 0; };
  const depthEntries = () => {
    const jobs = []; scene.traverse(o => { if (o.isMesh && o.castShadow) for (const m of [].concat(o.material)) jobs.push({ object: o, material: o.customDepthMaterial ?? depthOf(m) }); }); return jobs;
  };
  return { uniforms, depthEntries, warm: () => warmPrograms(renderer, scene, sync(), base, depthEntries(), false), render: () => refresh(true), update, invalidate, track, setSize, setTier, stats, matrix: sun.shadow.matrix, get version() { return version; },
    dispose() { rt.dispose(); base.dispose(); for (const d of cache.values()) d.dispose(); cache.clear(); entries.clear(); swaps.length = 0; for (const o of dynamic) o.layers.disable(DYN_LAYER); roots.clear(); always.clear(); promoted.clear(); rigid.clear(); } };
}

async function loadUnits(url) {
  const response = await fetch(url); if (!response.ok) throw new Error(`lightmap ${response.status}: ${url}`);
  let buf = await response.arrayBuffer();
  // format 2 is gzipped (unless the server already unpacked it), with 16-bit vertex maps and indices where they fit
  const b = new Uint8Array(buf, 0, 2);
  if (b[0] === 0x1f && b[1] === 0x8b) buf = await new Response(new Blob([buf]).stream().pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
  const hl = new DataView(buf).getUint32(0, true);
  const header = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 4, hl)));
  const base = 4 + hl;
  // the chunks are written back to back without padding, so copy rather than view (alignment)
  const take = (o, n, wide) => wide ? new Uint32Array(buf.slice(o, o + n * 4)) : Uint32Array.from(new Uint16Array(buf.slice(o, o + n * 2)));
  for (const u of header.units) {
    let o = base + u.off; const x32 = !u.x16, i32 = !u.i16;   // format 1 has neither flag: all 32-bit
    u.xref = take(o, u.nv, x32); o += u.nv * (x32 ? 4 : 2);
    u.index = take(o, u.ni, i32); o += u.ni * (i32 ? 4 : 2);
    u.uv = new Uint16Array(buf.slice(o, o + u.nv * 4));
    if (u.xref.length !== u.nv || u.index.length !== u.ni || u.uv.length !== u.nv * 2 || u.xref.some(i => i >= u.vc) || u.index.some(i => i >= u.nv)) throw new Error('Invalid lightmap UV unit');
  }
  return header;
}

// A copy of the geometry with seams split for the lightmap, and the lightmap UVs in uv1.
function relayout(geometry, unit) {
  const g = new THREE.BufferGeometry(), x = unit.xref;
  for (const [name, a] of Object.entries(geometry.attributes)) {
    const n = a.itemSize, out = new a.array.constructor(x.length * n);
    for (let i = 0; i < x.length; i++) for (let k = 0; k < n; k++) out[i * n + k] = a.array[x[i] * n + k];
    g.setAttribute(name, new THREE.BufferAttribute(out, n, a.normalized));
  }
  const uv = new Float32Array(x.length * 2);
  // the HDR file is read with flipY, so the lightmap's v is mirrored (checked visually: ?lmflip=0 scrambles every chart)
  const flip = new URLSearchParams(location.search).get('lmflip') !== '0';
  for (let i = 0; i < x.length; i++) { uv[i * 2] = unit.uv[i * 2] / 65535; uv[i * 2 + 1] = flip ? 1 - unit.uv[i * 2 + 1] / 65535 : unit.uv[i * 2 + 1] / 65535; }
  g.setAttribute('uv1', new THREE.BufferAttribute(uv, 2));
  g.setIndex(new THREE.BufferAttribute(unit.index, 1));
  for (const grp of geometry.groups) g.addGroup(grp.start, grp.count, grp.materialIndex);
  g.computeBoundingBox(); g.computeBoundingSphere();
  return g;
}

// An RGBM lightmap PNG as an 8-bit texture: no premultiplied alpha, no colour management, rows flipped like the HDR
// files were, so the same lightmap UVs apply. A split layer (15 MB file cap) is uploaded part by part into one texture.
// Tone-curved atlases get a full mip chain (three allocates it and fills it with an encoded box filter, which
// linearMips then replaces); RGBM (no encoding.json, legacy) stays single-level.
async function loadRGBM(url, rows, renderer, mips = true, track) {
  const image = await lightmapBitmap(url, rows, track), opts = { flipY: false, premultiplyAlpha: false, colorSpace: THREE.NoColorSpace, channel: 1, generateMipmaps: mips, minFilter: mips ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter, magFilter: THREE.LinearFilter, anisotropy: mips ? 4 : 1 };
  if (Math.max(image.width, image.height) > renderer.capabilities.maxTextureSize) { if (image.parts) image.parts.forEach(p => p.bitmap.close()); else image.close(); throw new Error('lightmap atlas exceeds maxTextureSize'); }
  if (!image.parts) { const t = Object.assign(new THREE.Texture(image), opts); t.needsUpdate = true; t.userData.restore = () => renderer.initTexture?.(t); return t; }
  const t = Object.assign(new THREE.DataTexture(null, image.width, image.height), opts);
  const parts = image.parts.map(p => ({ ...p, texture: new THREE.Texture(p.bitmap), offset: new THREE.Vector3(0, p.y, 0) }));
  const upload = () => {
    // allocate every level, then copy the parts without a generateMipmap per part (linearMips fills the levels)
    t.generateMipmaps = mips; t.source.dataReady = false; t.needsUpdate = true; renderer.initTexture(t);
    t.generateMipmaps = false;
    try { for (const p of parts) renderer.copyTextureToTexture(p.texture, t, null, p.offset); } finally { t.generateMipmaps = mips; }
  };
  t.userData.restore = upload;
  t.userData.release = () => { for (const p of parts) { p.texture.dispose(); p.bitmap.close(); } };
  try {
    if (Math.max(image.width, image.height) > renderer.capabilities.maxTextureSize) throw new Error('lightmap atlas exceeds maxTextureSize');
    upload(); return t;
  } catch (err) { t.dispose(); t.userData.release(); throw err; }
}
// Magnified (a texel spans more than a pixel): decode each of the four nearest level-0 texels BEFORE bilinear mixing,
// so sharp baked shadow edges interpolate in linear light. Minified: hardware trilinear/anisotropic filtering over the
// linear-light mip chain (linearMips), decoded after; the blend between neighbouring texels then covers less than a
// pixel. ?lmfilter=decode (always level 0, decoded first) / hw (always hardware) for A/B checks.
const LM_FILTER = { decode: 1, hw: 2 }[new URLSearchParams(location.search).get('lmfilter')] ?? 0;
const LM_SAMPLE = lmSampleGLSL(LM_FILTER);

// Mip levels in linear light for a tone-curved pair [slot, lamps] (GPU, once per upload): each level-k texel decodes
// its 2x2 block of level k-1, averages it weighted by coverage (texels the bake wrote: level 0 has a non-black slot
// texel, chart padding included; deeper levels carry the covered fraction in alpha), re-encodes with the layer's K,
// and an empty texel next to a covered one takes their average (fresh padding, alpha 0, never averaged further), so
// distant chart edges neither darken into the black gaps nor mix with unrelated charts beyond one texel. The lamps layer
// uses the slot layer's coverage (lamp light is legitimately black in much of the covered atlas). Like
// bake/scripts/downscale_atlas.py, but per level and with the bake's padding standing in for its coverage mask.
// ?lmmips=gl keeps three's encoded box filter instead.
const MIP_FS = `#version 300 es
precision highp float; precision highp int; precision highp sampler2D;
uniform sampler2D uSrc; uniform sampler2D uCov; uniform int uFirst; uniform float uK; uniform ivec2 uDst;
out vec4 outColor;
vec3 dec( vec3 y ) { vec3 y2 = y * y; return uK * y2 / max( 1.0 - y2, vec3( 1.0 / 255.0 ) ); }
vec3 enc( vec3 x ) { return sqrt( x / ( x + uK ) ); }
vec4 block( ivec2 d ) {
  ivec2 hi = textureSize( uSrc, 0 ) - 1; vec4 s = vec4( 0.0 );
  for ( int j = 0; j < 2; j ++ ) for ( int i = 0; i < 2; i ++ ) {
    ivec2 q = min( d * 2 + ivec2( i, j ), hi ); vec4 t = texelFetch( uSrc, q, 0 );
    float c = uFirst == 1 ? ( any( greaterThan( texelFetch( uCov, q, 0 ).rgb, vec3( 0.0 ) ) ) ? 1.0 : 0.0 ) : t.a;
    s += vec4( dec( t.rgb ) * c, c );
  }
  return s;
}
void main() {
  ivec2 d = ivec2( gl_FragCoord.xy ); vec4 s = block( d );
  if ( s.a > 0.0 ) { outColor = vec4( enc( s.rgb / s.a ), max( s.a * 0.25, 1.0 / 255.0 ) ); return; }
  vec4 n = vec4( 0.0 );
  for ( int j = -1; j <= 1; j ++ ) for ( int i = -1; i <= 1; i ++ ) {
    ivec2 e = d + ivec2( i, j );
    if ( ( i != 0 || j != 0 ) && all( greaterThanEqual( e, ivec2( 0 ) ) ) && all( lessThan( e, uDst ) ) ) n += block( e );
  }
  outColor = vec4( n.a > 0.0 ? enc( n.rgb / n.a ) : vec3( 0.0 ), 0.0 );
}`;
function linearMips(renderer, [slot, lamps], K) {
  if (new URLSearchParams(location.search).get('lmmips') === 'gl') return null;
  const gl = renderer.getContext?.(), t0 = performance.now();
  if (!gl?.createShader || !renderer.properties) return null;   // test doubles
  for (const t of [slot, lamps]) renderer.initTexture(t);
  const shader = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
  const vs = shader(gl.VERTEX_SHADER, '#version 300 es\nvoid main() { vec2 p = vec2( float( ( gl_VertexID << 1 ) & 2 ), float( gl_VertexID & 2 ) ); gl_Position = vec4( p * 2.0 - 1.0, 0.0, 1.0 ); }');
  const fs = shader(gl.FRAGMENT_SHADER, MIP_FS), prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
  const vao = gl.createVertexArray(), fb = gl.createFramebuffer(); let levels = 0;
  try {
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('lightmap mips: ' + (gl.getShaderInfoLog(fs) || gl.getProgramInfoLog(prog)));
    gl.useProgram(prog); gl.bindVertexArray(vao); gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    for (const cap of [gl.BLEND, gl.DEPTH_TEST, gl.CULL_FACE, gl.SCISSOR_TEST, gl.STENCIL_TEST, gl.RASTERIZER_DISCARD]) gl.disable(cap);
    gl.colorMask(true, true, true, true);
    const u = n => gl.getUniformLocation(prog, n);
    gl.uniform1i(u('uSrc'), 0); gl.uniform1i(u('uCov'), 1);
    const slotGL = renderer.properties.get(slot).__webglTexture;
    for (const [t, k] of [[slot, K[0]], [lamps, K[1]]]) {
      const tex = renderer.properties.get(t).__webglTexture, w = t.image.width, h = t.image.height, n = Math.floor(Math.log2(Math.max(w, h))) + 1;
      gl.uniform1f(u('uK'), k);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, slotGL);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex);
      try {
        for (let level = 1; level < n; level++) {
          const lw = Math.max(1, w >> level), lh = Math.max(1, h >> level);
          // sample only the level below (no feedback loop with the level being written)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, level - 1); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, level - 1);
          gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, level);
          if (level === 1 && gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) throw new Error('lightmap mips: framebuffer incomplete');
          gl.uniform1i(u('uFirst'), level === 1 ? 1 : 0); gl.uniform2i(u('uDst'), lw, lh);
          gl.viewport(0, 0, lw, lh); gl.drawArrays(gl.TRIANGLES, 0, 3); levels++;
        }
      } finally {
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, 0); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, 1000);
      }
    }
    return { levels, ms: +(performance.now() - t0).toFixed(1) };
  } catch (err) {
    console.warn('lightmap mips: keeping the encoded mip chain', err); return null;
  } finally {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.deleteFramebuffer(fb); gl.bindVertexArray(null); gl.deleteVertexArray(vao);
    gl.useProgram(null); gl.deleteProgram(prog); gl.deleteShader(vs); gl.deleteShader(fs);
    renderer.resetState();
  }
}

const lmResolution = bakedResolution;
const disposeAtlas = t => { t.dispose(); t.userData.release?.(); t.image.close?.(); };
const atlasMiB = (w, h) => w * h * 8 / 1048576;

export async function applyLightmaps({ scene, renderer, lighting, movables = [], dynamic = [], base = 'assets/lightmaps', slot = 'sunrise', lamps = 0.25, tier = 'laptop', onProgress = () => {} }) {
  const t0 = performance.now(), patchStart = shaderPatchCount(); let disposed = false;
  const legacyTune = { ...lighting.tune }, legacyGrade = Object.fromEntries(Object.entries(lighting.grade ?? {}).map(([k, u]) => [k, u.value]));
  const legacyTone = renderer.toneMapping, legacyExposure = renderer.toneMappingExposure, legacyAO = lighting.gtao?.blendIntensity;
  const legacySunCamera = lighting.sun.shadow.camera.clone();
  const originalBackground = scene.background, originalMeshes = new Map(), shadowCasters = new Map(), lightVisibility = new Map();
  // The shipped v2 tone-curved bake replaces the retired ?lmv=1 experiment.
  const v2 = true, dir = `${base}/v2`;
  let completed = 0;
  const progress = () => onProgress(++completed / 9);
  const asset = await lightmapAssets(base); progress();
  const availableSlots = await discoverSlots(base, asset.manifest);
  slot = initialSlot(slot, availableSlots);
  const multi = availableSlots.length > 1;
  if (multi) lamps = SLOTS[slot].lamps;
  let slots = null;
  // the compact formats when present: gzipped 16-bit UV data, tone-curved lossless WebP layers
  let enc = v2 ? await fetch(asset(`${dir}/${slot}/encoding.json`)).then(r => r.ok ? r.json() : null).catch(() => null) : null;
  if (!v2 || !enc || !SLOTS[slot] || !['slot', 'lamps'].every(k => Number.isFinite(enc.K?.[k]) && enc.K[k] > 0)) throw new Error('Invalid baked encoding or slot');
  progress();
  // Switchable lamps (lamp_groups.js): one baked layer per group of light switches, each at its physical level when on.
  // Without them (missing, invalid, failed to load, ?lmgroups=0) the shared lamps layer at the slot's level.
  let groupMeta = lampGroupsWanted() ? await fetch(asset(`${dir}/lampgroups/encoding.json`)).then(r => r.ok ? r.json() : null).catch(() => null) : null;
  if (groupMeta && !validGroupEncoding(groupMeta)) { console.warn('lamp groups: invalid encoding, using the shared lamps layer'); groupMeta = null; }
  let groups = groupMeta ? createLampGroups({ meta: groupMeta, switches: lighting.lightSwitches, dir, asset, renderer }) : null;
  let resolution = v2 && enc ? lmResolution(tier) : 'full', shadowSize = Math.min(TIERS[tier].shadowMap, renderer.capabilities.maxTextureSize);
  // A missing tier is a failed bake, never a reason to allocate the full atlas on a small device.
  const loadAtlases = async (res, name = slot, meta = enc, track = work => work()) => {
    const divisor = { full: 1, half: 2, quarter: 4 }[res];
    if (header.atlas.some(n => Math.ceil(n / divisor) > renderer.capabilities.maxTextureSize)) throw new Error('lightmap atlas exceeds maxTextureSize');
    const path = `${dir}/${name}${res === 'full' ? '' : '/' + res}`;
    const readLayer = (layer, folder, encoding) => {
      const rows = res === 'full' ? encoding?.rows?.[layer] : null;
      return loadRGBM(rows ? rows.map((_, i) => asset(`${folder}/${layer}_${i}.webp`)) : asset(`${folder}/${layer}.webp`), rows, renderer, true, track);
    };
    const lampsAtlas = async () => {
      if (groups) { const t = new THREE.DataTexture(new Uint8Array(4), 1, 1); t.needsUpdate = true; t.userData.unused = true; return t; }   // lamp groups instead
      if (name === 'sunrise') return readLayer('lamps', path, meta);
      const ownK = meta.K.lamps;
      const own = () => { meta.K.lamps = ownK; return readLayer('lamps', path, meta); };
      const shared = async () => {
        const encoding = await track(() => fetch(asset(`${dir}/sunrise/encoding.json`)).then(r => r.ok ? r.json() : null));
        if (!validEncoding(encoding)) throw Error('Invalid shared lamp encoding');
        const t = await readLayer('lamps', `${dir}/sunrise${res === 'full' ? '' : '/' + res}`, encoding);
        meta.K.lamps = encoding.K.lamps; return t;
      };
      if (meta.lampsSlot === 'sunrise') { try { return await shared(); } catch { return own(); } }
      try { return await own(); } catch { return shared(); }
    };
    const result = await Promise.allSettled([readLayer('slot', path, meta), lampsAtlas()]);
    if (result.some(r => r.status === 'rejected')) {
      for (const r of result) if (r.status === 'fulfilled') disposeAtlas(r.value);
      const reason = result.find(r => r.status === 'rejected').reason;
      throw reason;
    }
    const pair = Object.assign(result.map(r => r.value), { res });
    if (pair.some(t => !t.userData.unused && (t.image.width !== Math.ceil(header.atlas[0] / divisor) || t.image.height !== Math.ceil(header.atlas[1] / divisor)))) { pair.forEach(disposeAtlas); throw new Error('lightmap atlas dimensions disagree with UV data'); }
    if (enc) { pair.mips = linearMips(renderer, pair, [meta.K.slot, meta.K.lamps]); for (const t of pair) t.userData.mips = pair.mips; }
    return pair;
  };
  // UVs carry the authoritative full atlas size. Reject an oversized tier before decoding/uploading any atlas.
  const units = asset(`${dir}/lightmap_uv2.bin.gz`);
  const header = await Promise.resolve(units).then(loadUnits); progress();
  const scale = { full: 1, half: 2, quarter: 4 }[resolution];
  const expected = header.atlas.map(n => Math.ceil(n / scale));
  if (expected.some(n => !Number.isInteger(n) || n <= 0 || n > renderer.capabilities.maxTextureSize)) throw new Error('lightmap atlas exceeds maxTextureSize');
  const tasks = [
    loadProbes(asset(`${base}/${slot}/probes.bin`)),
    new HDRLoader().setDataType(THREE.HalfFloatType).loadAsync(asset(`${base}/${slot}/sky_bg.hdr`)),
    loadReflections(`${base}/${slot}/refl`, asset),
    v2 ? loadAtlases(resolution) : Promise.all(['sky', 'sunind', 'lamps'].map(l => new HDRLoader().setDataType(THREE.HalfFloatType).loadAsync(asset(`${base}/${slot}/${l}.hdr`)))),
    loadFoliage(asset(`${dir}/${slot}/foliage.bin.gz`)),
    lighting.outputPass && new URLSearchParams(location.search).get('agx') !== 'three' ? loadAgXLuts() : Promise.resolve(null),
  ];
  const groupTask = groups ? Promise.all([groups.setResolution(resolution), groupMeta.probes ? loadGroupProbes(asset(`${dir}/lampgroups/probes.bin`), groupMeta.groups.length, groupMeta.probes.count) : null,
    groupMeta.refl && lampReflSet(tier) ? loadLampReflections(dir, asset, groupMeta, lampReflSet(tier)).catch(err => { console.warn('lamp reflections failed: daylight reflections only', err); return null; }) : null])
    .catch(err => { console.warn('lamp groups failed, using the shared lamps layer', err); return false; }) : Promise.resolve(null);
  const staged = await Promise.allSettled(tasks.map(p => p.then(value => { progress(); return value; })));
  const release = (i, fn) => { if (staged[i].status === 'fulfilled' && staged[i].value) fn(staged[i].value); };
  const releaseStage = () => {
    release(1, t => t.dispose()); release(2, r => r.uniforms.uReflTex.value.dispose());
    release(3, ts => ts.forEach(disposeAtlas)); release(5, ts => ts.forEach(t => t.dispose()));
  };
  if (staged.some(r => r.status === 'rejected')) { releaseStage(); groupTask.then(() => groups?.dispose()); throw staged.find(r => r.status === 'rejected').reason; }
  let [probes, skyBg, refl, layers, foliageData, agxLuts] = staged.map(r => r.value);
  const groupData = await groupTask;
  if (groups && (groupData === false || (groupData[1] && probes.N !== groupMeta.probes.count))) {
    // fall back to the shared lamps layer: the pair was loaded without it
    groups.dispose(); groups = null; groupMeta = null; layers.forEach(disposeAtlas);
    try { layers = await loadAtlases(resolution); } catch (err) { releaseStage(); throw err; }
  } else if (groups) { groups.probeSH = groupData[1]; if (groupData[2]) attachLampReflections(refl, groupData[2], groups, multi); }
  // The plan's wall pieces (patches applied, garage annex included); the boxes are reused across slots.
  const walls = probeWalls(PROBE_WALL_PIECES), volume = buildProbeVolume(probes, groups, walls);
  volume.wet = SLOTS[slot].wet ?? 0; volume.refl = refl; volume.multi = multi; refl.slots = multi;
  // Every fetch/decode/validation has completed. Nothing above changes scene objects or legacy lighting.
  await new Promise(resolve => setTimeout(resolve, 0));
  if (v2) resolution = layers.res;
  const lmUniforms = { uLmK: { value: new THREE.Vector2(enc?.K.slot ?? 1, enc?.K.lamps ?? 1) }, uLmLamps: { value: v2 ? layers[1] : null }, uLampLevel: { value: lamps } };
  // one combined atlas: sky + sun bounce + lamps x level
  // multiplier from the bake's units to three's irradiance (?lmi= overrides it for calibration)
  const lmi = Number(new URLSearchParams(location.search).get('lmi')), lightMapIntensity = Number.isFinite(lmi) && lmi > 0 ? lmi : Math.PI;
  const target = { texture: layers[0] };
  let lampLevel = lamps, volumeRef = null;
  const combine = level => {
    if (disposed) return;
    lampLevel = level; lmUniforms.uLampLevel.value = level; if (slots) slots.combine(level); else volumeRef?.fill(level);
  };
  combine(lamps);

  // match units to live meshes: by id, then the leftovers by vertex count and tight world bounds (as the export measured
  // them; a transformed local box is too wide when rotated), never onto originals a merged mesh replaced or carried pieces
  const byId = new Map(), byCount = new Map();
  for (const u of header.units) { if (u.id) byId.set(u.id, u); if (!byCount.has(u.vc)) byCount.set(u.vc, []); byCount.get(u.vc).push(u); }
  const weather = new Map(), emissionCopies = new Map();
  // a copy follows its source's emission: intensity (switch, level) and colour (lamp_groups.js: the group's light)
  const syncEmissionCopies = level => { for (const [m, e] of emissionCopies) { m.emissiveIntensity = bakedEmission(e.record, e.source, e.base, level); if (m !== e.source) m.emissive.copy(e.source.emissive); } };
  const box = new THREE.Box3(), tmp = new THREE.Vector3(), mats = new Map(); let matched = 0, byBox = 0, triangles = 0;
  const pcss = createSunPCSS(renderer, scene, lighting.sun, shadowSize), legacyShadow = lighting.sun.userData.bakedShadow;
  pcss.track(movables, false, true); pcss.track(dynamic);   // movable furniture (rigid), doors and toggles: their sun shadows follow them
  pcss.setTier(tier);
  lighting.sun.userData.bakedShadow = true;
  const restoreEmission = lighting.physicalEmission?.();   // as in the bake: only real light sources glow (the manifest's emits)
  scene.updateMatrixWorld(true);
  const meshes = []; scene.traverse(o => { if (o.isMesh && !o.isInstancedMesh && !o.isSkinnedMesh && o.geometry?.attributes.position) meshes.push(o); });
  const tight = o => { box.makeEmpty(); const pos = o.geometry.attributes.position; for (let i = 0; i < pos.count; i++) box.expandByPoint(tmp.fromBufferAttribute(pos, i).applyMatrix4(o.matrixWorld)); return box; };
  const pairs = new Map();
  for (const o of meshes) { const u = byId.get(o.userData.lmId); if (u && !u.used && u.vc === o.geometry.attributes.position.count) { u.used = true; pairs.set(o, u); } }
  for (const o of meshes) {
    if (pairs.has(o) || o.userData.lmSkip || o.userData.batched) continue;
    const list = byCount.get(o.geometry.attributes.position.count); if (!list?.some(u => !u.used)) continue;
    tight(o); const u = list.find(u => !u.used && Math.abs(box.min.x - u.min[0]) + Math.abs(box.min.y - u.min[1]) + Math.abs(box.min.z - u.min[2]) + Math.abs(box.max.x - u.max[0]) + Math.abs(box.max.y - u.max[1]) + Math.abs(box.max.z - u.max[2]) < 0.006);
    if (u) { u.used = true; pairs.set(o, u); byBox++; }
  }
  // Hinged and bifold door leaves take the probe volume in every pose, lit from the side each face looks at (probeLit,
  // sided). The bake fits only the pose they were exported in (a swung leaf carried the closet's dark into the room),
  // and their thin raised panels and louvre slats bake with dark rims at lightmap resolution that Cycles does not show;
  // the probes match the Cycles renders of these doors closer, open and closed. The glazed garden doors (french,
  // sliders) keep the bake: their frames stand in open sunlight the coarse outdoor probes underestimate. The manifest
  // says which (lighting_roles.js: why 'door').
  const doorParts = new Set(), sharedUV = new Set();
  for (const o of meshes) if (meshLighting(o).why === 'door') doorParts.add(o);
  for (const o of doorParts) if (pairs.has(o)) { pairs.get(o).used = false; pairs.delete(o); }
  // The exporter writes one glTF mesh for meshes that share geometry and material, and an unwrap that kept the
  // lightmap UVs on that shared primitive left Blender only the last unit's layout: all of them baked into one chart,
  // the others' charts stayed empty (the louvered bifold leaves, black and speckled). Such meshes take the probe volume
  // until a bake whose sidecar says every unit had its own primitive (unwrap.py, "ownPrimitives").
  if (!header.ownPrimitives) {
    const byMesh = new Map();
    for (const o of pairs.keys()) { const k = o.geometry.uuid + ':' + [].concat(o.material).map(m => m?.uuid).join(':'); if (!byMesh.has(k)) byMesh.set(k, []); byMesh.get(k).push(o); }
    for (const list of byMesh.values()) if (list.length > 1) for (const o of list) { pairs.get(o).used = false; pairs.delete(o); sharedUV.add(o); }
  }
  for (const [o, u] of pairs) {
    originalMeshes.set(o, { geometry: o.geometry, material: o.material, castShadow: o.castShadow });
    matched++; triangles += u.ni / 3; tight(o);
    // the walkthrough kept the house shell out of the sun's shadow map (its fake daylight lit rooms instead); with
    // baked light only the live sun's direct light remains, and the shell must stop it at the walls and roof
    o.castShadow = true;
    o.geometry = relayout(o.geometry, u);
    const cls = meshLighting(o).weather;
    const lm = m => {
      if (!m?.isMeshStandardMaterial) return m;
      const key = m.uuid + (cls ?? '');
      if (mats.has(key)) return mats.get(key);
      // a clean copy: none of the walkthrough's fake-GI shader patches, the bake's light instead of the sky's diffuse
      const c = m.clone(); c.onBeforeRender = m.onBeforeRender;
      if (lightingOf(m).emits) emissionCopies.set(c, { record: lightingOf(m), source: m, base: m.emissiveIntensity });   // copies must follow their switch even with one slot
      if (multi) weather.set(c, { cls: cls === 'ground' ? null : cls, color: c.color.clone(), roughness: c.roughness });
      wetten(c, cls === 'ground' ? null : cls, SLOTS[slot].wet);   // ground wetness is per pixel
      if (!lightingOf(m).emits) c.emissiveIntensity = 0;   // a raster glow stand-in: the bake has the real light
      c.lightMap = target.texture; c.lightMapIntensity = lightMapIntensity;
      // the flags (shaders/features.js): wet ground where the mesh's after-rain class is not a plant's (the mask is per
      // pixel: outdoors, facing up, near the ground), reflections dimmed by the baked light reaching the spot (specular
      // occlusion: a dim room reflects little of the bright sky environment, a sunlit terrace keeps it all), the lamp
      // groups, the slot crossfade; and the options other modules set (baked_material.js)
      const own = ownShader(m), L = lightingOf(m);
      const features = self => bakedFeatures(L, { weather: cls }, { path: 'lightmap', multi, wet: multi ? 1 : SLOTS[slot].wet, refl,
        contact: CONTACT ? CONTACT_SHADER : null, lmEncoding: enc ? 'tone' : 'rgbm', lmFilter: LM_FILTER, lampGroups: groups ? groupMeta.textures : 0,
        ...materialOptions(self) });
      const bind = (s, f) => {
        Object.assign(s.uniforms, lmUniforms, slots?.uniforms, groups?.uniforms, pcss.uniforms);
        if (f.wet) s.uniforms.uWet = slots ? slots.wet : { value: SLOTS[slot].wet };
        if (f.refl) Object.assign(s.uniforms, refl.uniforms);
        if (f.contactSun || f.contactAO) Object.assign(s.uniforms, CONTACT_SHADER.uniforms);
        if (f.planarPuddle) Object.assign(s.uniforms, PUDDLE_UNIFORMS);
      };
      installBaked(c, { name: 'lightmap', tag: 'baked-v4', own, features, bind, renderer });
      mats.set(key, c); return c;
    };
    o.material = Array.isArray(o.material) ? o.material.map(lm) : lm(o.material);
  }
  // glass must not block the sun (in the bake shadow rays pass through it, as through real glass)
  scene.traverse(o => { if (o.isMesh && o.material && !meshLighting(o).sun.cast) { shadowCasters.set(o, o.castShadow); o.castShadow = false; } });
  lighting.sun.shadow.needsUpdate = true;
  // the live lights the bake now carries
  for (const l of [...lighting.windowLights, ...lighting.fixtures.children]) if (l.isLight) { lightVisibility.set(l, l.visible); l.visible = false; }
  scene.traverse(o => { if (o.isHemisphereLight) { lightVisibility.set(o, o.visible); o.visible = false; } });
  // Light in the raster look's units over the bake: the stand-in sprites (a sconce's wash, the fire's glow on the gravel) are
  // hidden, the bake carries that light; a live light outside the bake (the fire's) takes its lumens in the bake's units
  // (lighting_roles.js sprite, bakedLiveLevel). Here and only here.
  const standIns = new Map(), liveLights = [], lmPerW = groupMeta?.lmPerW ?? LM_PER_BAKE_UNIT;
  scene.traverse(o => {
    if (o.isLight) { const v = bakedLiveLevel(o, lmPerW); if (v !== null) { o.userData.bakedLevel = v; liveLights.push(o); } return; }
    for (const m of [].concat(o.material ?? [])) if (m && !standIns.has(m) && lightingOf(m)?.sprite === 'standIn') { standIns.set(m, m.visible); m.visible = false; }
  });
  // every lit surface that did not get a lightmap takes the probe volume (and the soft sun)
  let probeLitCount = 0, foliage = null, envTarget = null, windows = null, stats = null;
  const hooks = new Map(), envs = new Map(), sided = new Set();
  // doors and toggles (blinds, curtains, the garage door) sit in walls and windows: lit from the side each face looks at
  // (manifest probeSide 'face'; not their glass: its reflection keeps the occlusion of the cell it sits in, as every other pane).
  // The lookup is the mesh's, but probeLit patches a material: a face mesh whose probe-lit material another mesh also
  // uses (a door leaf in the frame's trim white) gets its own copy, so that mesh keeps the plain lookup.
  const sidedOf = m => m && !m.lightMap && !m.transparent && !(m.transmission > 0), faceUse = new Map();
  scene.traverse(o => { if (o.isMesh) { const face = meshLighting(o).probeSide === 'face'; for (const m of [].concat(o.material)) if (sidedOf(m)) faceUse.set(m, (faceUse.get(m) ?? 0) | (face ? 1 : 2)); } });
  const faceCopies = new Map(), faceSwaps = new Map();
  const faceCopy = m => {
    if (faceUse.get(m) !== 3) return m;
    let c = faceCopies.get(m);
    if (!c) { c = m.clone(); c.onBeforeCompile = m.onBeforeCompile; c.onBeforeRender = m.onBeforeRender; c.customProgramCacheKey = m.customProgramCacheKey; copyOwnShader(m, c); faceCopies.set(m, c); }
    return c;
  };
  scene.traverse(o => {
    if (!o.isMesh || meshLighting(o).probeSide !== 'face') return;
    const next = Array.isArray(o.material) ? o.material.map(faceCopy) : faceCopy(o.material);
    if (next !== o.material && (!Array.isArray(next) || next.some((m, i) => m !== o.material[i]))) { faceSwaps.set(o, o.material); o.material = next; }
    for (const m of [].concat(o.material)) if (sidedOf(m)) sided.add(m);
  });
  const register = object => {
    if (!object || disposed) return;
    object.traverse(o => {
      if (!o.isMesh) return;
      for (const m of [].concat(o.material)) {
        if (!m?.isMeshStandardMaterial || m.lightMap || m.isFogVolumeMaterial || !volume) continue;
        if (!hooks.has(m) && !m.userData.probeLit) {
          hooks.set(m, { emission: m.emissiveIntensity });   // its hook and key: baked_material.js keeps them
          const L = lightingOf(m);
          if (!L.emits) m.emissiveIntensity = 0;
          // an unswitched lamp follows the slot's lamp level as its lightmapped copies do (switched lamps: their switch)
          else if (L.emits.kind === 'lamp' && !L.emits.switched) emissionCopies.set(m, { record: L, source: m, base: m.emissiveIntensity });
          probeLit(m, volume, pcss, sided.has(m), renderer); probeLitCount++;
        }
        if (envTarget) setEnv(m);
      }
    });
    if (stats) stats.probeLit = probeLitCount;
    if (object === scene) pcss.invalidate(); else pcss.track(object, true);   // pets, the visitor: always-hot casters
  };
  const setEnv = m => {
    if (!envTarget) return;   // pending: rebuilt by the next afterTime (context restore)
    if (!envs.has(m)) envs.set(m, { map: m.envMap, intensity: m.envMapIntensity, rotation: m.envMapRotation?.clone() });
    if (m.envMap !== envTarget.texture) m.needsUpdate = true;
    m.envMap = envTarget.texture; m.envMapIntensity = 1; m.envMapRotation?.set(0, 0, 0);
  };
  if (volume) {
    foliage = await bakeFoliage(scene, asset(`${dir}/${slot}/foliage.bin.gz`), volume, foliageData ?? undefined);
    volume.fill(lampLevel); register(scene);
  }
  volumeRef = volume;
  // The planar puddle's ground (puddles.js): decided now, so its shaders compile with the planar puddle under the loading
  // screen and a quality change only switches the reflection on or off.
  const puddleScan = multi || SLOTS[slot].wet > 0 ? scanPuddles(scene, [...movables, ...dynamic]) : null;
  if (puddleScan) markPuddleGround(scene, puddleScan.ground);
  // every visible lit mesh left without a lightmap, by why: the manifest's reason (plant, cutout, glass, door, movable, dynamic:
  // animated or toggled), sharedUV, skipped by unwrap.py (ground, far), unmatched (an id the sidecar does not know: the
  // bake is older than the scene), nobake (a sidecar without ids)
  const unlit = {}, skipped = header.skipped ?? {}, shown = o => { for (let p = o; p; p = p.parent) if (!p.visible) return false; return true; };
  for (const o of meshes) {
    const ms = [].concat(o.material); if (!shown(o) || !ms.some(m => m?.isMeshStandardMaterial && !m.lightMap)) continue;
    const r = meshLighting(o), id = o.userData.lmId;
    const L0 = lightingOf(ms[0]), why = r.why === 'plant/glass' ? (L0?.foliage ? 'plant' : L0?.alphaCutout ? 'cutout' : 'glass') : r.why ?? (sharedUV.has(o) ? 'sharedUV' : !id ? 'dynamic' : skipped[id] ?? (header.units.some(u => u.id) ? 'unmatched' : 'nobake'));
    unlit[why] = (unlit[why] ?? 0) + 1;
  }
  // main calls after layout.animate, garden wind and all interaction/parent transform updates.
  // switch levels follow the switches (and the slot's default switch set, blended through a slot fade)
  const updateGroups = dt => { if (!groups) return; const fade = slots?.uniforms.uSlotBlend.value ?? 0; groups.update(dt, slots?.name ?? slot, fade > 0 ? slots.desired : undefined, fade); groups.applyGlow(); syncEmissionCopies(slots?.params.lamps ?? lamps); };
  // Fragment sampler audit (sampler_budget.js), once a second over the programs linked since: stats.samplers holds the
  // most samplers any baked program uses. A baked program that still failed to link over the texture units (the
  // estimate missed) has its materials compile again with a lower limit, so the next fit leaves more out.
  let auditClock = 1;
  const audit = () => {
    const a = auditPrograms(renderer);
    for (const f of a.failed) {
      if (f.handled) continue; f.handled = true;
      if (!/texture image units|samplers?/i.test(f.log)) { console.warn(`baked shader "${f.name}" failed to link:`, f.log); continue; }
      for (const m of [...mats.values(), ...hooks.keys()]) {
        if (renderer.properties.get(m).currentProgram !== f.program || (m.userData.samplerCut ?? 0) >= 8) continue;
        m.userData.samplerCut = (m.userData.samplerCut ?? 0) + 2; m.needsUpdate = true;
        console.warn(`baked shader "${m.name || m.type}": over the GPU's fragment texture units after linking; recompiling with ${m.userData.samplerCut} fewer`);
      }
    }
    if (stats) stats.samplers = { max: a.max, limit: a.limit, worst: a.worst, programs: a.programs, failed: a.failed.map(f => f.name) };
  };
  const checkPatches = () => assertShaderPatches(patchStart);
  // After startup a late failure (a pet, the visitor, a mirror compiled on first sight) leaves that one material stock:
  // shaderPatch already warned once, and dropping the whole walk to legacy mid-visit is the worse outcome.
  const updateMovables = dt => { if (!disposed) { slots?.update(dt); autoExposure?.update(dt); pcss.update(dt); if (slots) setFixtureGlow(slots.params.lamps); updateGroups(dt); whiteBalance?.update(dt); if ((auditClock += dt) >= 1 && renderer.info?.programs && renderer.getContext) { auditClock = 0; audit(); } } };
  stats = { units: header.units.length, matched, byId: matched - byBox, byBox, unusedUnits: header.units.length - matched, unlit, triangles, materials: mats.size, movables: movables.length, probes: probes ? probes.N : 0, probeLit: probeLitCount, foliage, ms: Math.round(performance.now() - t0) };
  const atlasStats = () => { if (v2) Object.assign(stats, { resolution, atlas: [target.texture.image.width, target.texture.image.height], atlasMiB: atlasMiB(target.texture.image.width, target.texture.image.height) * (enc ? 4 / 3 : 1), mips: target.texture.userData.mips ?? null }); stats.shadowSize = shadowSize; };
  atlasStats();
  if (skyBg) skyBg.mapping = THREE.EquirectangularReflectionMapping;
  // after the walkthrough sets its time of day: the bake's sun, its sky as the background, and a Blender-like grade
  let groupsReset = false;
  const afterTime = () => {
    if (disposed) return;
    if (groups && !groupsReset) { groupsReset = true; groups.reset(slots?.name ?? slot); updateGroups(0); }
    const mood = slots?.params ?? SLOTS[slot];
    const raw = new URLSearchParams(location.search).has('rawtone'), e = raw ? 1 : Math.pow(2, mood.ev);
    Object.assign(lighting.tune, { dayOutdoor: e, dayIndoor: e });
    applyBakedSun(lighting, renderer, mood, shadowSize);
    pcss.render();
    if (skyBg) {
      scene.background = skyBg; scene.backgroundIntensity = 1; scene.backgroundRotation.set(0, 0, 0);
      // reflections of the bake's sunrise sky (dimmed per pixel by the baked light that reaches it), not the midday map
      if (!envTarget) {
        const pmrem = new THREE.PMREMGenerator(renderer);
        try { envTarget = pmrem.fromEquirectangular(skyBg); slots?.setEnvironment(envTarget); }
        catch (err) { envTarget = null; console.warn('baked environment deferred', err?.message ?? err); }   // lost context: the restore retries
        finally { pmrem.dispose(); }
      }
      scene.traverse(o => { if (o.isMesh) for (const m of [].concat(o.material)) if (m?.isMeshStandardMaterial && (m.lightMap || m.userData.probeLit)) setEnv(m); });
    }
    if (skyBg && !windows) { windows = addWindowReflections(scene, skyBg, refl); stats.windowPanes = windows.count; stats.reflectionProbes = refl?.count ?? 0; }
    // the bake already holds the ambient occlusion; the live pass stays only for contact shadows
    if (lighting.gtao) lighting.gtao.blendIntensity = 0.35;
    // no lifted blacks; with three's AgX (?agx=three) more contrast and a little saturation stand in for Blender's
    // "Medium High Contrast" look, which Blender's own AgX (agx.js) already applies
    const threeAgX = new URLSearchParams(location.search).get('agx') === 'three';
    lighting.grade.lift.value = 0; lighting.grade.contrast.value = threeAgX ? 1.16 : 1; lighting.grade.saturation.value = threeAgX ? 1.08 : 1;
    lighting.update(0, true);   // snap the exposure instead of easing toward it
    if (new URLSearchParams(location.search).has('sunoff')) lighting.sun.intensity = 0;   // debugging: lightmaps and probes only
    // ?rawtone: plain sRGB of the linear frame, for calibrating against Blender's "Standard" view
    if (new URLSearchParams(location.search).has('rawtone')) {
      renderer.toneMapping = THREE.NoToneMapping; Object.assign(lighting.tune, { dayOutdoor: 1, dayIndoor: 1 }); lighting.update(0, true);
      Object.assign(lighting.grade, {}); lighting.grade.contrast.value = 1; lighting.grade.saturation.value = 1; lighting.grade.grain.value = 0;
    }
  };
  // Serialise atlas swaps: rapid J toggles keep only one pair in flight. Release GPU storage and decoded bitmaps.
  let wanted = resolution, pending = null;
  const setQuality = name => {
    if (disposed) return Promise.resolve();
    shadowSize = Math.min(TIERS[name].shadowMap, renderer.capabilities.maxTextureSize);
    const shadow = lighting.sun.shadow;
    if (shadow.mapSize.x !== shadowSize) { shadow.map?.dispose(); shadow.map = null; shadow.mapSize.set(shadowSize, shadowSize); shadow.needsUpdate = true; }
    pcss.setSize(shadowSize); pcss.setTier(name); stats.shadowSize = shadowSize;
    wanted = v2 && enc ? lmResolution(name) : 'full';
    groups?.setResolution(wanted).catch(err => console.warn('lamp groups: tier change failed', err));
    if (slots) return slots.setQuality(wanted);
    pending ??= (async () => {
      while (wanted !== resolution) {
        const next = wanted, pair = await loadAtlases(next);
        if (disposed || pair.res !== wanted || pair.res === resolution) { pair.forEach(disposeAtlas); if (disposed) break; continue; }   // superseded
        const old = layers.slice(); layers.splice(0, 2, ...pair); target.texture = pair[0]; lmUniforms.uLmLamps.value = pair[1];
        for (const m of mats.values()) m.lightMap = pair[0];
        old.forEach(disposeAtlas); resolution = pair.res; atlasStats();
      }
    })().finally(() => { pending = null; });
    return pending;
  };
  let puddleRefl = null;
  const setFeatures = name => {
    tier = name;
    // Puddles are decoration: if their GPU targets cannot be made (a lost context), the feature stays off and is retried by
    // the next setFeatures / the context restore, never thrown through the load.
    if (TIERS[name].puddles && !puddleRefl) {
      try { puddleRefl = createPuddles({ scene, renderer, wet: multi ? 1 : SLOTS[slot].wet, furniture: [...movables, ...dynamic], ground: puddleScan?.ground ?? null }); api.puddleRefl = puddleRefl; }
      catch (err) { console.warn('puddles deferred', err?.message ?? err); }
    }
    puddleRefl?.setEnabled(TIERS[name].puddles && (!multi || slots.params.wet > 0));
    setShafts(name);
  };
  // Volumetric light (volumetric.js): ?shafts / ?shafts=0, else the tier's flag. Built once in the linear chain (TAA's
  // internal composer when TAA is on), then switched with the tier. Its media follow the slot and the slot crossfade.
  let removeShafts = null;
  const shaftsState = () => {
    const f = slots?.fading;
    return { current: slots?.name ?? slot, next: f?.name ?? null, t: f?.t ?? 0, progress: f?.progress ?? 0, sky: skyBg, nextSky: f?.sky ?? null, exposureShift: slots?.exposureShift ?? 0 };
  };
  const setShafts = name => {
    const want = featureWanted(location.search, 'shafts', TIERS[name]) && !!lighting.renderConfig?.hdr;
    if (want && !api.shafts) {
      try {
        const pass = new VolumetricPass({ camera: lighting.camera, sun: lighting.sun, shadow: pcss, renderer, state: shaftsState });
        removeShafts = installVolume(lighting.taa?.internal ?? lighting.composer, pass); api.shafts = pass;
      } catch (err) { console.warn('volumetric light deferred', err?.message ?? err); }
    }
    if (api.shafts) api.shafts.enabled = want;
  };
  if (agxLuts) await blenderAgX(lighting.outputPass, undefined, agxLuts);
  const autoExposure = createBakedAutoExposure({ renderer, lighting, getSlot: () => slots?.params ?? SLOTS[slot], getTier: () => tier, fading: () => !!slots?.stepping });
  // the camera's white balance, from the probe volume's light on the surfaces in view (white_balance.js); with the auto
  // exposure only (a fixed exposure, ?capture, stays comparable with Cycles)
  const whiteBalance = autoExposure && createWhiteBalance({ lighting, renderer, volume: volume.uniforms, walls });
  const restore = () => { try { restoreBaked(); } catch (err) { console.warn('baked restore incomplete', err?.message ?? err); } };
  const restoreBaked = () => {
    if (disposed) return;
    if (slots) slots.restore();
    else for (const t of layers) t.userData.restore?.();
    if (!slots && v2 && enc) { const m = linearMips(renderer, layers, [enc.K.slot, enc.K.lamps]); for (const t of layers) t.userData.mips = m; }
    groups?.restore();
    pcss.invalidate(); lighting.sun.shadow.needsUpdate = true;
    if (!slots) { envTarget?.dispose(); envTarget = null; }
    if (!puddleRefl && TIERS[tier]) setFeatures(tier);   // puddles that failed while the context was lost
    afterTime(); };
  renderer.domElement?.addEventListener('webglcontextrestored', restore);
  const dispose = () => {
    if (disposed) return; disposed = true;
    renderer.domElement?.removeEventListener('webglcontextrestored', restore);
    autoExposure?.dispose(); whiteBalance?.dispose();
    scene.userData.contact?.dispose(); restoreEmission?.();
    Object.assign(lighting.tune ?? {}, legacyTune);
    for (const [k, v] of Object.entries(legacyGrade)) lighting.grade[k].value = v;
    renderer.toneMapping = legacyTone; renderer.toneMappingExposure = legacyExposure;
    if (lighting.gtao) lighting.gtao.blendIntensity = legacyAO;
    lighting.sun.shadow.camera.copy(legacySunCamera); lighting.sun.shadow.needsUpdate = true;
    removeShafts?.(); api.shafts?.dispose(); api.shafts = null;
    if (legacyShadow === undefined) delete lighting.sun.userData.bakedShadow; else lighting.sun.userData.bakedShadow = legacyShadow;
    groups?.dispose(); puddleRefl?.dispose(); windows?.dispose(); pcss.dispose(); if (!slots) envTarget?.dispose();
    if (multi || scene.background === skyBg) scene.background = originalBackground;
    if (slots) slots.dispose();
    else { skyBg?.dispose(); refl?.uniforms.uReflTex.value.dispose();
      for (const u of Object.values(volume?.uniforms ?? {})) if (u.value?.isTexture) u.value.dispose(); }
    for (const [m, e] of envs) { m.envMap = e.map; m.envMapIntensity = e.intensity; if (e.rotation) m.envMapRotation.copy(e.rotation); m.needsUpdate = true; }
    for (const [m, h] of hooks) { uninstallBaked(m); m.emissiveIntensity = h.emission; }
    foliage?.dispose();
    for (const [o, s] of originalMeshes) { o.geometry.dispose(); o.geometry = s.geometry; o.material = s.material; o.castShadow = s.castShadow; }
    for (const [o, m] of faceSwaps) o.material = m;
    for (const c of faceCopies.values()) c.dispose();
    for (const [o, v] of shadowCasters) if (!originalMeshes.has(o)) o.castShadow = v;
    for (const [l, v] of lightVisibility) l.visible = v;
    for (const [m, v] of standIns) m.visible = v;
    for (const l of liveLights) delete l.userData.bakedLevel;
    for (const m of mats.values()) m.dispose();
    if (!slots) layers.forEach(disposeAtlas);
  };
  if (groups) stats.lampGroups = { groups: groupMeta.groups.length, textures: groupMeta.textures, switches: groups.switches, matched: groups.matched, resolution: groups.resolution, probes: !!groups.probeSH, mips: groups.texture?.userData.mips ?? null };
  const api = { checkPatches, warmDepth: async () => { await pcss.warm(); checkPatches(); }, lampGroups: groups, stats, auditSamplers: () => { audit(); return stats.samplers; }, combine, target, slot: SLOTS[slot], slotName: slot, availableSlots, sunShadow: pcss, updateMovables, afterTime, setQuality, register, dispose, puddleRefl, setFeatures };
  // Fixture switches update before this hook each frame. Apply the preset's physical lamp glow last.
  // With lamp groups each switch drives its own fixtures' glow (groups.applyGlow); the preset level only applies to the
  // single shared lamps layer. Either way the lightmapped copies (what actually renders) follow their sources afterwards.
  const setFixtureGlow = level => {
    if (!groups) for (const { toggle } of lighting.lightSwitches ?? []) for (const g of toggle.glows) {
      const property = g.wash ? 'opacity' : 'emissiveIntensity';
      g.material[property] = glowLevel(g, { baked: true, level: level * (toggle.manual ? toggle.value : 1) });
    }
    syncEmissionCopies(level);
  };
  if (multi) {
    const stageSlot = async (name, res, onProgress) => {
      const progress = createSlotProgress(onProgress), track = work => progress.track(work);
      const meta = await track(() => fetch(asset(`${dir}/${name}/encoding.json`)).then(r => r.ok ? r.json() : null));
      if (!validEncoding(meta)) throw Error('Invalid destination encoding');
      const results = await Promise.allSettled([
        loadAtlases(res, name, meta, track), loadProbes(asset(`${base}/${name}/probes.bin`), track),
        track(() => new HDRLoader().setDataType(THREE.HalfFloatType).loadAsync(asset(`${base}/${name}/sky_bg.hdr`))),
        loadReflections(`${base}/${name}/refl`, asset, track), loadFoliage(asset(`${dir}/${name}/foliage.bin.gz`), track),
      ]);
      const cleanup = () => { if (results[0].status === 'fulfilled') results[0].value.forEach(disposeAtlas); if (results[2].status === 'fulfilled') results[2].value.dispose(); if (results[3].status === 'fulfilled') results[3].value.uniforms.uReflTex.value.dispose(); };
      if (results.some(r => r.status === 'rejected')) { cleanup(); throw results.find(r => r.status === 'rejected').reason; }
      const [pair, P, sky, reflections, fol] = results.map(r => r.value);
      sky.mapping = THREE.EquirectangularReflectionMapping;
      let nextVolume, env;
      try {
        nextVolume = buildProbeVolume(P, groups, walls); nextVolume.fill(SLOTS[name].lamps);
        const pmrem = new THREE.PMREMGenerator(renderer); try { env = pmrem.fromEquirectangular(sky); } finally { pmrem.dispose(); }
        progress.finish();
        return { name, enc: meta, layers: pair, volume: nextVolume, sky, refl: reflections, foliage: fol, env, resolution: pair.res,
          remip: () => linearMips(renderer, pair, [meta.K.slot, meta.K.lamps]) };
      } catch (err) { cleanup(); env?.dispose(); for (const u of Object.values(nextVolume?.uniforms ?? {})) if (u.value?.isTexture) u.value.dispose(); throw err; }
    };
    const initialLayers = layers.slice(), initialK = [enc.K.slot, enc.K.lamps];
    // A slot fade blends in exposed space (lighting_slots.js fadeExposure): the two slots' exposures for this view, of which
    // only the difference counts. The fixed exposure (?capture): the slots' ev. The visitor outdoors: the garden's (ev +
    // outdoor offset), which its exposure holds. In a room the visitor's exposure is the meter's, every frame of the fade
    // (auto_exposure.js uFade), so the radiance blends linearly there; in a doorway the two mix as the exposure target does.
    const exposureBases = (a, b) => {
      if (!autoExposure) return [a.ev, b.ev];
      const w = THREE.MathUtils.clamp(lighting.indoorWeight(lighting.camera.position), 0, 1);
      return [0, ((b.ev + (b.autoExposure.outdoor ?? 0)) - (a.ev + (a.autoExposure.outdoor ?? 0))) * (1 - w)];
    };
    // the sun through a crossfade: a dissolve between the two slots' suns, not a sweep (sun_crossfade.js; ?sunsweep=1: the
    // sweep, for comparison). Its depth map is redrawn when its direction changes, once per crossfade.
    const sweep = new URLSearchParams(location.search).has('sunsweep');
    let sunAt = null;
    slots = createSlotRuntime({ scene, renderer, available: availableSlots, load: stageSlot, disposeAtlas, foliage, exposureBases,
      initial: { name: slot, enc, layers, volume, sky: skyBg, refl, foliage: foliageData, resolution,
        remip: () => linearMips(renderer, initialLayers, initialK) },
      setParams(mood) {
        const raw = new URLSearchParams(location.search).has('rawtone'), f = slots?.fading;
        const sun = f && !sweep ? crossfadeSun(mood, SLOTS[slots.name], SLOTS[f.name], f.t) : mood;
        applyBakedSun(lighting, renderer, raw ? { ...sun, ev: 0 } : sun, shadowSize);
        if (!f || sun.az !== sunAt?.az || sun.el !== sunAt?.el) { sunAt = { az: sun.az, el: sun.el }; pcss.invalidate(); }
        if (raw) renderer.toneMapping = THREE.NoToneMapping;
        if (new URLSearchParams(location.search).has('sunoff')) lighting.sun.intensity = 0;
        const exposure = raw ? 1 : 2 ** mood.ev; Object.assign(lighting.tune, { dayOutdoor: exposure, dayIndoor: exposure });
        for (const [m, w] of weather) { m.color.copy(w.color); m.roughness = w.roughness; wetten(m, w.cls, mood.wet); }
        puddleRefl?.setEnabled(TIERS[tier].puddles && mood.wet > 0);
        lighting.update(0, true);
        setFixtureGlow(mood.lamps);
      },
      commit(data) {
        const changedSlot = slot !== data.name;   // quality reloads and context restores keep manual switches
        slot = data.name; enc = data.enc; skyBg = data.sky; resolution = data.resolution;
        layers.splice(0, 2, ...data.layers); target.texture = data.layers[0];
        lmUniforms.uLmK.value.set(enc.K.slot, enc.K.lamps); lmUniforms.uLmLamps.value = data.layers[1];
        for (const m of mats.values()) m.lightMap = target.texture;
        envTarget = data.env ?? envTarget;
        scene.traverse(o => { if (o.isMesh) for (const m of [].concat(o.material)) if (m?.isMeshStandardMaterial && (m.lightMap || m.userData.probeLit)) setEnv(m); });
        api.slot = SLOTS[slot]; api.slotName = slot; atlasStats();
        if (groups) { if (changedSlot) groups.reset(slot); updateGroups(0); }   // only a new slot restores defaults
      },
    });
    volume.slots = slots; Object.assign(lmUniforms, slots.uniforms);
    api.slots = slots;   // read-only for validation (tools/fade_check.mjs --trace: the fade's weight, progress and exposure bases)
  }
  // Optional callback: (fraction, { name, loaded, total, phase }); transition marks the start of the fade.
  api.setSlot = (name, onProgress) => slots ? slots.request(name, onProgress) : Promise.resolve(name === slot);
  api.nextSlot = () => nextSlot(slots?.desired ?? slot, availableSlots);
  try {
    const materials = new Set([...mats.values(), ...hooks.keys(), ...pcss.depthEntries().map(e => e.material)]);
    for (const m of materials) { dryCompile(m, renderer); checkPatches(); await shaderYield(); }
  } catch (err) { dispose(); throw err; }
  return api;
}

// Probe grid for movable objects: per point, order-2 SH of incoming radiance (three's basis and frame) per layer.
async function loadProbes(url, track = work => work()) {
  const buf = await track(async () => {
    const response = await fetch(url); if (!response.ok) throw new Error(`probes ${response.status}: ${url}`);
    return response.arrayBuffer();
  });
  const hl = new DataView(buf).getUint32(0, true), header = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 4, hl)));
  const N = header.count; let o = 4 + hl;
  const valid = new Uint8Array(buf.slice(o, o + N)); o += N; o = (o + 3) & ~3;
  const layers = {}; for (const l of header.layers) { layers[l] = new Float32Array(buf.slice(o, o + N * 27 * 4)); o += N * 27 * 4; }
  if (!Number.isInteger(N) || N < 4 || o !== buf.byteLength || valid.length !== N || valid.filter(Boolean).length < 4 || !['sky', 'sunind', 'lamps'].every(l => layers[l]?.length === N * 27) || !header.points || header.points.length !== N) throw new Error('Invalid baked probes');
  const points = new Float32Array(header.points.flat());
  return { N, valid, layers, points };
}

// ---- Reflection probes: full-lighting panoramas baked in Blender at points through the house and garden, each with a
// box from raycasts to its surroundings. Per pixel: the probe whose box holds the point (else the nearest), the
// reflected ray intersected with that box and looked up from the probe's centre (parallax correction), sampled at a
// blur level set by roughness. The patio then reflects the house and chairs, floors their own room, windows the garden.
async function loadReflections(dir, asset, track = work => work()) {
  const meta = (await track(async () => {
    const response = await fetch(asset(`${dir}/meta.json`)); if (!response.ok) throw new Error(`reflections ${response.status}`);
    return response.json();
  })).sort((a, b) => a.i - b.i);
  if (!meta.length || meta.some(m => !['p', 'min', 'max'].every(k => m[k]?.length === 3 && m[k].every(Number.isFinite)))) throw new Error('Invalid reflection metadata');
  const { w, h, n, data } = await loadPanoramas(meta.map(m => asset(`${dir}/probe_${String(m.i).padStart(2, '0')}.hdr`)), track);
  const tex = new THREE.DataArrayTexture(data, w, h, n);
  Object.assign(tex, { format: THREE.RGBAFormat, type: THREE.HalfFloatType, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, wrapS: THREE.RepeatWrapping, wrapT: THREE.ClampToEdgeWrapping });
  tex.needsUpdate = true;
  // Flat Float32Arrays, not Vector3 arrays: three re-uploads a material's uniforms on every material switch, and an
  // array of vectors is first copied element by element into a scratch buffer each time (3 x 26 vectors, ~650 switches
  // in the garden view: ~10 ms of its frame). A typed array goes to WebGL as it is.
  const v3 = k => Float32Array.from(meta.flatMap(m => m[k]));
  return { count: n, key: String(n), uniforms: { uReflTex: { value: tex }, uReflPos: { value: v3('p') }, uReflMin: { value: v3('min') }, uReflMax: { value: v3('max') }, uReflMips: { value: Math.floor(Math.log2(Math.max(w, h))) } } };
}
// Equirect HDR panoramas -> one half-float RGBA buffer of layers, rows bottom-up (v = 0 first), as an array texture is
// uploaded without flipY.
async function loadPanoramas(urls, track) {
  const results = await Promise.allSettled(urls.map(url => track(() => new HDRLoader().setDataType(THREE.HalfFloatType).loadAsync(url))));
  if (results.some(r => r.status === 'rejected')) { for (const r of results) if (r.status === 'fulfilled') r.value.dispose(); throw results.find(r => r.status === 'rejected').reason; }
  const maps = results.map(r => r.value);
  const w = maps[0].image.width, h = maps[0].image.height, n = maps.length;
  if (maps.some(t => t.image.width !== w || t.image.height !== h || t.image.data.length !== w * h * 4)) { maps.forEach(t => t.dispose()); throw new Error('Invalid reflection image dimensions'); }
  const data = new Uint16Array(w * h * 4 * n);
  maps.forEach((t, k) => {
    const src = t.image.data, ch = src.length / (w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const si = ((h - 1 - y) * w + x) * ch, di = ((k * h + y) * w + x) * 4;
      data[di] = src[si]; data[di + 1] = src[si + 1]; data[di + 2] = src[si + 2]; data[di + 3] = 0x3c00;
    }
    t.dispose();
  });
  return { w, h, n, data };
}
// Lamp reflections (reflprobe.py --lamps, lamp_groups_pack.py refl): per reflection probe, panoramas of its brightest
// lamp groups alone and one of its other visible groups, at the bake's physical level (no sky, no sun). The daylight
// probes hold no lamp light; each lamp layer adds its light weighed by its groups' live levels (switches, slot defaults,
// fades), so a mirror shows only the lamps that are on. Layers are matched to groups by name: the group count may change.
// Which set: every layer (hq), or one panorama per probe with all its lamps (laptop, mobile: a third of the memory, its
// weight the light-weighted level of its groups); ?refllamps=all|merged|0 overrides.
const lampReflSet = tier => {
  const q = new URLSearchParams(location.search).get('refllamps');
  if (['0', 'off'].includes(q)) return null;
  return q === 'all' || q === 'merged' ? q : tier === 'hq' ? 'all' : 'merged';
};
async function loadLampReflections(dir, asset, groupMeta, set = 'all', track = work => work()) {
  const full = await track(async () => {
    const r = await fetch(asset(`${dir}/lampgroups/${groupMeta.refl.meta}`)); if (!r.ok) throw new Error(`lamp reflections ${r.status}`);
    return r.json();
  });
  const meta = set === 'merged' && Array.isArray(full?.merged) ? { ...full, layers: full.merged } : full;
  const index = new Map(groupMeta.groups.map(g => [g.name, g.index]));
  if (meta?.version !== 1 || !Number.isInteger(meta.probes) || !Array.isArray(meta.layers) || !meta.layers.length ||
      meta.layers.some((l, i) => !Number.isInteger(l.probe) || l.probe < 0 || l.probe >= meta.probes || (i && l.probe < meta.layers[i - 1].probe) || typeof l.file !== 'string' || !l.groups || !Object.keys(l.groups).length))
    throw new Error('Invalid lamp reflection metadata');
  const range = new Float32Array(2 * meta.probes); let max = 0;
  meta.layers.forEach((l, i) => { if (!range[2 * l.probe + 1]) range[2 * l.probe] = i; max = Math.max(max, ++range[2 * l.probe + 1]); });
  const mix = meta.layers.map(l => Object.entries(l.groups).filter(([name, f]) => index.has(name) && f > 0).map(([name, f]) => [index.get(name), f]));
  const unknown = new Set(meta.layers.flatMap(l => Object.keys(l.groups)).filter(n => !index.has(n)));
  if (unknown.size) console.warn('lamp reflections: unknown lamp groups', [...unknown]);
  const { w, h, n, data } = await loadPanoramas(meta.layers.map(l => asset(`${dir}/lampgroups/refl/${l.file}`)), track);
  return { count: n, max, probes: meta.probes, w, h, data, range, mix };
}
// The lamp layers join the probes' array texture after the daylight layers (one sampler for both); in a multi-slot
// site after both slot halves (lightmap_slots.js reflArray). Weights per layer follow the lamp groups' levels.
function attachLampReflections(refl, lamps, groups, multi) {
  const day = refl.uniforms.uReflTex.value;
  if (lamps.probes !== refl.count || lamps.w !== day.image.width || lamps.h !== day.image.height) { console.warn('lamp reflections do not match the reflection probes: daylight reflections only'); return; }
  const weights = new Float32Array(4 * Math.ceil(lamps.count / 4));
  const update = () => { lamps.mix.forEach((m, l) => { let w = 0; for (const [g, f] of m) w += groups.levels[g] * f; weights[l] = w; }); };
  Object.assign(refl.uniforms, { uReflLamp: { value: lamps.range }, uReflLampW: { value: weights }, uReflLampBase: { value: (multi ? 2 : 1) * refl.count } });
  refl.lamps = { count: lamps.count, max: lamps.max, data: lamps.data };
  refl.key = `${refl.count}:${lamps.count}:${lamps.max}`;
  update(); groups.volumes.add(update);
  if (!multi) {
    const layer = day.image.width * day.image.height * 4, data = new Uint16Array(layer * (refl.count + lamps.count));
    data.set(day.image.data); data.set(lamps.data, layer * refl.count);
    const t = new THREE.DataArrayTexture(data, day.image.width, day.image.height, refl.count + lamps.count);
    for (const k of ['format', 'type', 'generateMipmaps', 'minFilter', 'magFilter', 'wrapS', 'wrapT']) t[k] = day[k];
    t.needsUpdate = true; day.dispose(); refl.uniforms.uReflTex.value = t;
  }
}

// ---- Probe volume: the probe grid resampled onto a regular 0.5 m 3D texture over the lot (three heights), first-order
// spherical harmonics per colour. Every lit surface without a lightmap (plants, the few unmatched meshes, movable
// furniture, pets) samples it per pixel by world position, so a carried chair or a cat takes the baked light of
// wherever it is, and foliage is lit by the same sunrise sky as the bake instead of the walkthrough's midday sky map.
const VOL = plan.volumes.probe;
// Plan pieces already exclude openings (solid sides, sills and lintels). Inset 1 cm to allow face contact.
const PROBE_WALL_PIECES = planPieces;
const probeWalls = pieces => pieces.map(p => [p.x0 + 0.01, p.x1 - 0.01, p.z0 + 0.01, p.z1 - 0.01, -p.y1 + 0.01, -p.y0 - 0.01]).filter(b => b[0] < b[1] && b[2] < b[3] && b[4] < b[5]);
function probeBlocked(px, py, pz, qx, qy, qz, walls) {
  const x0 = Math.min(px, qx), x1 = Math.max(px, qx), y0 = Math.min(py, qy), y1 = Math.max(py, qy), z0 = Math.min(pz, qz), z1 = Math.max(pz, qz);
  const dx = qx - px, dy = qy - py, dz = qz - pz;
  for (const b of walls) {
    if (x1 < b[0] || x0 > b[1] || y1 < b[2] || y0 > b[3] || z1 < b[4] || z0 > b[5]) continue;
    let lo = 0, hi = 1;
    for (let a = 0; a < 3; a++) {
      const p = a === 0 ? px : a === 1 ? py : pz, d = a === 0 ? dx : a === 1 ? dy : dz;
      if (d === 0) { if (p < b[a * 2] || p > b[a * 2 + 1]) { hi = -1; break; } }
      else { const t0 = (b[a * 2] - p) / d, t1 = (b[a * 2 + 1] - p) / d; lo = Math.max(lo, Math.min(t0, t1)); hi = Math.min(hi, Math.max(t0, t1)); }
      if (lo > hi) break;
    }
    if (lo <= hi) return true;
  }
  return false;
}
function buildProbeVolume(P, groups = null, walls = []) {
  const NX = Math.round((VOL.x1 - VOL.x0) / VOL.step) + 1, NZ = Math.round((VOL.y1 - VOL.y0) / VOL.step) + 1, NY = VOL.heights.length;
  const valid = []; for (let i = 0; i < P.N; i++) if (P.valid[i]) valid.push(i);
  // Walk outwards in x: once dx alone exceeds the 16th distance, all remaining probes are farther away.
  valid.sort((a, b) => P.points[a * 3] - P.points[b * 3] || a - b);
  const starts = new Int32Array(NX); let start = 0;
  for (let x = 0; x < NX; x++) { while (start < valid.length && P.points[valid[start] * 3] < VOL.x0 + x * VOL.step) start++; starts[x] = start; }
  // Test only the nearest 16, stopping at 4 visible probes; reuse scratch storage across cells.
  const K = Math.min(16, valid.length), ids = new Int32Array(K), ds = new Float64Array(K), picks = new Int32Array(4);
  // per cell, the 4 nearest visible probes and their weights (fixed); the lamp level only changes the sum
  const near = new Int32Array(NX * NZ * NY * 4), wts = new Float32Array(NX * NZ * NY * 4);
  for (let y = 0; y < NY; y++) for (let z = 0; z < NZ; z++) for (let x = 0; x < NX; x++) {
    const px = VOL.x0 + x * VOL.step, py = VOL.heights[y], pz = -(VOL.y0 + z * VOL.step);
    ds.fill(Infinity);
    let left = starts[x] - 1, right = starts[x];
    while (left >= 0 || right < valid.length) {
      const dl = left >= 0 ? px - P.points[valid[left] * 3] : Infinity, dr = right < valid.length ? P.points[valid[right] * 3] - px : Infinity;
      if (Math.min(dl * dl, dr * dr) > ds[K - 1]) break;
      const i = dl <= dr ? valid[left--] : valid[right++];
      const dx = P.points[i * 3] - px, dy = (P.points[i * 3 + 1] - py) * 2, dz = P.points[i * 3 + 2] - pz, d = dx * dx + dy * dy + dz * dz;
      if (d > ds[K - 1] || (d === ds[K - 1] && i >= ids[K - 1])) continue;
      let k = K - 1;
      while (k > 0 && (d < ds[k - 1] || (d === ds[k - 1] && i < ids[k - 1]))) { ds[k] = ds[k - 1]; ids[k] = ids[k - 1]; k--; }
      ds[k] = d; ids[k] = i;
    }
    let count = 0;
    for (let k = 0; k < K && count < 4; k++) {
      const i = ids[k] * 3;
      if (!probeBlocked(px, py, pz, P.points[i], P.points[i + 1], P.points[i + 2], walls)) picks[count++] = k;
    }
    // Inside walls (or outside the probe coverage), retain distance-only light if every candidate is blocked.
    if (!count) { count = 4; for (let k = 0; k < 4; k++) picks[k] = k; }
    const c = ((y * NZ + z) * NX + x) * 4; let sum = 0;
    for (let k = 0; k < 4; k++) { const j = picks[k % count]; near[c + k] = ids[j]; wts[c + k] = k < count ? 1 / (ds[j] + 0.02) : 0; sum += wts[c + k]; }
    for (let k = 0; k < 4; k++) wts[c + k] /= sum;
  }
  // One texture for the three colours: R, G and B stacked along its depth (3 x NY slices), one sampler per slot instead
  // of three (Apple GPUs allow 16 per fragment shader). probe.glsl.js clamps the depth inside each colour's slices.
  // Half floats: linearly filtered in every WebGL2 (iOS has no OES_texture_float_linear), ample range for irradiance SH.
  const tex = new THREE.Data3DTexture(new Uint16Array(NX * NZ * NY * 3 * 4), NX, NZ, NY * 3);
  Object.assign(tex, { format: THREE.RGBAFormat, type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping, wrapR: THREE.ClampToEdgeWrapping, unpackAlignment: 1 });
  // lamp groups (lamp_groups.js): per group, its luminance SH resampled to the cells once; a fill weighs them by the
  // groups' levels and colours, so a switch refills the volume (lamp_groups.js volumes) in a few milliseconds
  const cells = NX * NZ * NY, sh = groups?.probeSH, G = sh ? groups.groups.length : 0, cellG = sh ? new Float32Array(G * cells * 4) : null;
  if (sh) for (let g = 0; g < G; g++) for (let c = 0; c < cells; c++) for (let k = 0; k < 4; k++) {
    let v = 0; for (let j = 0; j < 4; j++) v += wts[c * 4 + j] * sh[(g * P.N + near[c * 4 + j]) * 9 + k];
    cellG[(g * cells + c) * 4 + k] = v;
  }
  let last = 0;
  const fill = (lamps = last) => {
    last = lamps; const gw = sh ? groups.probeWeights : null;
    for (let c = 0; c < cells; c++) for (let ch = 0; ch < 3; ch++) for (let k = 0; k < 4; k++) {
      let v = 0;
      for (let j = 0; j < 4; j++) { const i = near[c * 4 + j], o = i * 27 + k * 3 + ch; v += wts[c * 4 + j] * (P.layers.sky[o] + P.layers.sunind[o] + (gw ? 0 : lamps * P.layers.lamps[o])); }
      if (gw) for (let g = 0; g < G; g++) { const w = gw[g * 3 + ch]; if (w) v += w * cellG[(g * cells + c) * 4 + k]; }
      tex.image.data[(ch * cells + c) * 4 + k] = toHalf(v);
    }
    tex.needsUpdate = true;
  };
  if (sh) { const refill = () => fill(); groups.volumes.add(refill); tex.addEventListener('dispose', () => groups.volumes.delete(refill)); }
  // texture coordinates: cell centres sit at the texel centres
  const uniforms = { uProbeSH: { value: tex },
    uProbeOrigin: { value: new THREE.Vector3(VOL.x0, VOL.heights[0], -VOL.y0) }, uProbeScale: { value: new THREE.Vector3(1 / VOL.step, 1 / (VOL.heights[1] - VOL.heights[0]), -1 / VOL.step) },
    uProbeDims: { value: new THREE.Vector3(NX, NY, NZ) } };
  return { fill, uniforms, near, wts };
}
const toHalf = THREE.DataUtils.toHalfFloat;
function probeLit(material, volume, pcss, sided = false, renderer = null) {
  if (material.userData.probeLit) return;
  material.userData.probeLit = true;
  const own = ownShader(material);
  // the material's manifest record (lighting_roles.js): leaf cards, the plant treatment, wet ground, sheer cloth, mirrors;
  // the slot runtime joins the volume after registration, so the flags are read when compiled
  const L = lightingOf(material), refl = volume.refl, fol = material.foliageBake;
  // (and the options other modules set: the planar puddle, the puddle reflection's copies, TAA; baked_material.js)
  const features = self => bakedFeatures(L, { probeSide: sided ? 'face' : 'none' }, { path: 'probe', multi: !!volume.slots, wet: volume.multi ? 1 : volume.wet,
    refl, contact: CONTACT ? CONTACT_SHADER : null, foliageBake: !!fol, ...materialOptions(self) });
  const bind = (s, f) => {
    Object.assign(s.uniforms, volume.uniforms, pcss.uniforms);
    if (f.wet) s.uniforms.uWet = volume.slots ? volume.slots.wet : { value: volume.wet };
    if (f.refl) Object.assign(s.uniforms, refl.uniforms);
    if (f.contactSun || f.contactAO) Object.assign(s.uniforms, CONTACT_SHADER.uniforms);
    if (f.sheer) s.uniforms.uSheer = material.sheerUniform ??= { value: new THREE.Vector2(L.sheer.reflect, L.sheer.transmit) };
    if (f.foliageBake) Object.assign(s.uniforms, FOLIAGE_UNIFORMS, f.slotFade ? volume.slots.uniforms : {});
    if (f.planarPuddle) Object.assign(s.uniforms, PUDDLE_UNIFORMS);
  };
  installBaked(material, { name: 'probe', tag: 'probe-v4', own, features, bind, renderer });
  material.needsUpdate = true;
}

// Window reflections. The walkthrough's glass is nearly transparent, and three scales a transparent surface's
// reflection by its opacity, so the panes reflected almost nothing. Real glass reflects about 4% head-on and far more
// at grazing angles regardless of what it lets through, so each pane gets an additive overlay: Schlick Fresnel times
// the bake's sky, with directions at or below the horizon taking the dark green of the garden instead.
function addWindowReflections(scene, sky, refl) {
  const material = new THREE.ShaderMaterial({
    uniforms: { tSky: { value: sky }, uGround: { value: new THREE.Color(0.035, 0.045, 0.03) }, ...(refl ? refl.uniforms : {}) },
    vertexShader: `varying vec3 vWorld; varying vec3 vNormalW;
      void main() { vec4 w = modelMatrix * vec4( position, 1.0 ); vWorld = w.xyz; vNormalW = normalize( mat3( modelMatrix ) * normal ); gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `#include <common>
      ${refl ? reflGLSL(refl.count, !!refl.slots, false, refl.lamps) : ''}
      uniform sampler2D tSky; uniform vec3 uGround; varying vec3 vWorld; varying vec3 vNormalW;
      void main() {
        vec3 v = normalize( vWorld - cameraPosition ), n = normalize( vNormalW ) * ( gl_FrontFacing ? 1.0 : -1.0 );
        if ( dot( n, v ) > 0.0 ) n = -n;
        vec3 r = reflect( v, n );
        float c = clamp( dot( -v, n ), 0.0, 1.0 ), F = 0.04 + 0.96 * pow( 1.0 - c, 5.0 );
        vec2 uv = vec2( atan( r.z, r.x ) * RECIPROCAL_PI2 + 0.5, asin( clamp( r.y, -1.0, 1.0 ) ) * RECIPROCAL_PI + 0.5 );
        vec3 refl = ${refl ? 'reflProbe( vWorld, r, 0.0 )' : 'mix( uGround, texture2D( tSky, uv ).rgb, smoothstep( -0.02, 0.25, r.y ) )'};
        gl_FragColor = vec4( F * refl, 1.0 );
      }`,
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false,
  });
  const panes = [], add = [];
  scene.traverse(o => { if (o.isMesh && !o.userData.reflection && [].concat(o.material).some(m => m && lightingOf(m).glass?.overlay)) add.push(o); });
  // a pane in a static subtree never recomposes its children's matrices (batch.js), so the overlay takes its world
  // matrix once here; left at identity it would float at the world origin (the pale rectangle on the kitchen island)
  for (const o of add) { const r = new THREE.Mesh(o.geometry, material); r.userData.reflection = true; r.renderOrder = (o.renderOrder || 0) + 1; r.raycast = () => {}; o.add(r); r.updateMatrixWorld(true); panes.push(r); }
  return { count: panes.length, dispose() { for (const r of panes) r.removeFromParent(); material.dispose(); } };
}

// Put the live sun where the bake had it (direct light only; its bounce is in the lightmap).
export function applyBakedSun(lighting, renderer, s, shadowSize = lighting.sun.shadow.mapSize.x) {
  const a = THREE.MathUtils.degToRad(s.az), e = THREE.MathUtils.degToRad(s.el), sun = lighting.sun;
  sun.position.copy(sun.target.position).add(new THREE.Vector3(Math.sin(a) * Math.cos(e), Math.sin(e), -Math.cos(a) * Math.cos(e)).multiplyScalar(60));
  sun.color.setRGB(...s.color); sun.intensity = s.strength;
  // a low sun throws long shadows from trees well outside the lot: cover +-40 m (2 cm per texel at 4096)
  Object.assign(sun.shadow.camera, { left: -40, right: 40, top: 40, bottom: -40, near: 1, far: 140 });
  sun.position.copy(sun.target.position).add(new THREE.Vector3(Math.sin(a) * Math.cos(e), Math.sin(e), -Math.cos(a) * Math.cos(e)).multiplyScalar(90));
  sun.shadow.camera.updateProjectionMatrix();
  if (sun.shadow.mapSize.x !== shadowSize) { sun.shadow.mapSize.set(shadowSize, shadowSize); sun.shadow.map?.dispose(); sun.shadow.map = null; }
  sun.shadow.needsUpdate = true;
  // Blender's AgX + "Medium High Contrast" look, as in the Cycles references (agx.js); ?agx=three for three's AgX
  renderer.toneMapping = THREE.AgXToneMapping; renderer.toneMappingExposure = Math.pow(2, s.ev);
  // The output transform was staged and installed by applyLightmaps before startup can finish.
}

// Internals exported for node tests (no runtime use).
export { PROBE_WALL_PIECES, probeWalls, probeBlocked, buildProbeVolume, VOL, probeLit, reflGLSL, loadProbes, loadReflections, createSunPCSS, TONE_DECODE, RGBM_DECODE, LM_SAMPLE, loadRGBM, attachLampReflections };
